import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { getItems, createItem, updateItem, deleteItem, ensureFIGVault, searchCarriers, FIG_LISTS } from '../../api/figApi';
import { parseLocalDate } from '../../utils/parseLocalDate';
import { currentFirst, sortByEffectiveDesc } from '../../utils/policySelect';
import { lossRunLines, lossRunDirectory, termOf, dateOnlyOf, sortRequestsNewestFirst } from '../../utils/lossRunStatus';
import {
  LOSS_RUN_METHODS, emptyRequestForm, requestFormFromRow, validateRequestForm, minutesLogged, saveLossRunRequest,
  policyIdsToSelected, selectedToPolicyIds,
} from '../../utils/lossRunForm';
import { AUTH_ON_FILE_OPTIONS, authFormFromInsured, validateAuthForm, saveAuthorization } from '../../utils/lossRunAuth';
import { loadLossRunData } from '../../utils/lossRunLoad';
import { buildCoverageGrid, placePopover } from '../../utils/lossRunGrid';
import { buildTodo } from '../../utils/lossRunTodo';
import { buildClaudePrompt } from '../../utils/lossRunPrompt';
import {
  currentSeason, resolutionIndex, splitResolutions, resolutionSentence, openSummary, resolutionCoversPolicy,
  emptyResolutionForm, validateResolutionForm, saveResolution, RESOLUTION_STATUSES, RESOLUTION_REASONS,
} from '../../utils/lossRunResolutions';
import { buildHandoffModel } from '../../utils/buildHandoffModel';
import { generateHandoffPDF, downloadPdfBytes } from '../../utils/lossRunHandoffPdf';
import { buildScoreboard, scoreboardSummary } from '../../utils/lossRunScoreboard';
import { getBrandAssets } from '../../utils/brandAssets';
import ACORD125Page1 from './ACORD125Page1';
import generateACORD25 from './generateACORD25';
import ACORD28 from './ACORD28';
import ACORD126 from './ACORD126';
import ACORD140 from './ACORD140';
import ACORD130 from './ACORD130';
import FIGFolioExport from './FIGFolioExport';
import {
  POLICY_LOB_OPTIONS, POLICY_STATUS_OPTIONS, INVOICE_FEES_OPTIONS, emptyPolicy,
} from './policyFormOptions';
import './FIG.css';

// ACORD form registry — only forms with built components
const ACORD_FORMS = {
  '125': { name: 'ACORD 125', desc: 'Commercial Insurance Application', view: '125', blank: '/acord/ACORD_125_blank.pdf' },
  '25':  { name: 'ACORD 25',  desc: 'Certificate of Liability Insurance', view: '25', blank: '/acord/ACORD_25_blank.pdf' },
  '28':  { name: 'ACORD 28',  desc: 'Evidence of Commercial Property Insurance', view: '28', blank: '/acord/ACORD_28_blank.pdf' },
  '126': { name: 'ACORD 126', desc: 'Commercial General Liability Section', view: '126', blank: '/acord/ACORD_126_blank.pdf' },
  '140': { name: 'ACORD 140', desc: 'Property Section', view: '140', blank: '/acord/ACORD_140_blank.pdf' },
  '130': { name: 'ACORD 130', desc: 'Workers Compensation Application', view: '130', blank: '/acord/ACORD_130_blank.pdf' },
};

// LOB → which ACORD forms are relevant (by key in ACORD_FORMS)
const ACORD_LOB_MAP = {
  'Commercial Property': ['28', '140'],
  'General Liability': ['126'],
  'Commercial General Liability': ['126'],
  'Workers Compensation': ['130'],
  'Commercial Auto': [],
  'Business Owners Policy': [],
  'Umbrella': [],
  'Excess Liability': [],
};

// Forms available regardless of LOB
const UNIVERSAL_FORM_KEYS = ['125', '25'];

// FIG_Policies.LOB value → FIGVault subfolder name under Clients/{Company}/Policies/
const LOB_TO_VAULT_FOLDER = {
  'commercial general liability': 'GL',
  'general liability': 'GL',
  'commercial property': 'CP',
  'workers compensation': 'WC',
  'commercial auto': 'CA',
  'foreign liability': 'Travel',
  'umbrella/excess': 'Umbrella',
  'inland marine': 'IM',
  'crime': 'Crime',
  'cyber': 'Cyber',
  'epli': 'EPLI',
  'd&o': 'DO',
  'professional liability': 'PL',
  'pollution': 'Pollution',
};

function lobToVaultFolder(lob) {
  if (!lob) return null;
  const key = String(lob).trim().toLowerCase();
  return LOB_TO_VAULT_FOLDER[key] || null;
}

// Document checklist per policy — matched by filename substring (case-insensitive)
const POLICY_DOC_CHECKLIST = [
  { label: 'Quote', test: /quote/i },
  { label: 'Binder', test: /binder/i },
  { label: 'Dec Page', test: /dec[\s_-]*page|declaration/i },
  { label: 'Loss Run', test: /loss[\s_-]*run/i },
  { label: 'Endorsements', test: /endorse/i },
];

const TABS = [
  'Profile',
  'Locations & Buildings',
  'People',
  'Vehicles & Equipment',
  'Policies',
  'Loss Runs',
  'Additional Interests',
  'History',
  'ACORDs',
  'Application',
];

const PROFILE_SECTIONS = [
  {
    label: 'Company Info',
    fields: [
      { key: 'CompanyName', label: 'Company Name' },
      { key: 'DBA', label: 'DBA' },
      { key: 'EntityType', label: 'Entity Type', options: [
        '', 'Corporation', 'Individual', 'LLC', 'Partnership',
        'Joint Venture', 'Not For Profit Org', 'Subchapter S Corporation',
        'Trust', 'Other',
      ] },
      { key: 'FEIN', label: 'FEIN' },
      { key: 'GLCode', label: 'GL Code' },
      { key: 'WebsiteAddress', label: 'Website' },
      { key: 'BusinessPhone', label: 'Business Phone' },
      { key: 'DateBusinessStarted', label: 'Date Business Started', type: 'date' },
      { key: 'Status', label: 'Status' },
      { key: 'Principal', label: 'Principal' },
    ],
  },
  {
    label: 'Classification',
    fields: [
      { key: 'SICCode', label: 'SIC Code' },
      { key: 'NAICSCode', label: 'NAICS Code' },
      { key: 'NAICSDescription', label: 'NAICS Description' },
      { key: 'NCCIRiskId', label: 'NCCI Risk ID' },
      { key: 'CreditBureauName', label: 'Credit Bureau Name' },
      { key: 'NatureOfBusiness', label: 'Nature of Business' },
      { key: 'OperationsDescription', label: 'Operations Description', textarea: true },
    ],
  },
  {
    label: 'Mailing Address',
    fields: [
      { key: 'MailingAddress', label: 'Address' },
      { key: 'MailingCity', label: 'City' },
      { key: 'MailingState', label: 'State' },
      { key: 'MailingZip', label: 'Zip' },
    ],
  },
  {
    label: 'Billing Address',
    fields: [
      { key: 'BillingAddress', label: 'Address' },
      { key: 'BillingCity', label: 'City' },
      { key: 'BillingState', label: 'State' },
      { key: 'BillingZip', label: 'Zip' },
    ],
  },
  {
    label: 'Primary Contact',
    fields: [
      { key: 'PrimaryFirstName', label: 'First Name' },
      { key: 'PrimaryLastName', label: 'Last Name' },
      { key: 'PrimaryTitle', label: 'Title' },
      { key: 'PrimaryEmail', label: 'Email' },
      { key: 'PrimaryPhone', label: 'Phone' },
    ],
  },
  {
    label: 'Operations',
    fields: [
      { key: 'AnnualRevenue', label: 'Annual Revenue', type: 'number' },
      { key: 'AnnualPayroll', label: 'Annual Payroll', type: 'number' },
      { key: 'SubcontractorCosts', label: 'Subcontractor Costs', type: 'number' },
      { key: 'EmployeeCount', label: 'Employee Count', type: 'number' },
    ],
  },
];

function FIGInsured({ insured, getToken, onBack, initialTab, initialPolicyId, initialPolicy }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'Profile');
  const [form, setForm] = useState(() => {
    const initial = {};
    for (const section of PROFILE_SECTIONS) {
      for (const f of section.fields) {
        initial[f.key] = insured[f.key] ?? '';
      }
    }
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [generatingCoi, setGeneratingCoi] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [acordView, setAcordView] = useState(null);

  // --- Collapsible profile sections ---
  const [profileCollapsed, setProfileCollapsed] = useState({
    'Company Info': false, 'Classification': true, 'Mailing Address': true,
    'Billing Address': true, 'Primary Contact': true, 'Operations': true, 'Documents': false,
  });
  function toggleProfileSection(label) {
    setProfileCollapsed(prev => ({ ...prev, [label]: !prev[label] }));
  }

  // --- Documents section ---
  const GRAPH = 'https://graph.microsoft.com/v1.0';
  const [vaultDocs, setVaultDocs] = useState(null); // { folderName: [files] }
  const [vaultDocsLoading, setVaultDocsLoading] = useState(false);
  const docCollapsedStorageKey = `fig-doc-collapsed-${insured.id}`;
  const [folioOpen, setFolioOpen] = useState(false);
  const [policyGroupCollapsed, setPolicyGroupCollapsed] = useState(() => {
    try {
      const raw = sessionStorage.getItem(docCollapsedStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(docCollapsedStorageKey, JSON.stringify(policyGroupCollapsed)); }
    catch { /* ignore quota/private-mode errors */ }
  }, [policyGroupCollapsed, docCollapsedStorageKey]);

  const loadVaultDocs = useCallback(async () => {
    if (!insured.CompanyName) return;
    setVaultDocsLoading(true);
    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);
      const basePath = `Clients/${insured.CompanyName}`;
      const result = {};

      // List top-level folders
      const topRes = await fetch(
        `${GRAPH}/drives/${driveId}/root:/${basePath}:/children?$select=name,id,folder,file,webUrl,lastModifiedDateTime,size&$top=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!topRes.ok) { setVaultDocs({}); return; }
      const topData = await topRes.json();
      const items = topData.value || [];

      for (const item of items) {
        if (item.folder) {
          // It's a subfolder — list its contents
          const subRes = await fetch(
            `${GRAPH}/drives/${driveId}/items/${item.id}/children?$select=name,id,folder,file,webUrl,lastModifiedDateTime,size&$top=100`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (subRes.ok) {
            const subData = await subRes.json();
            const subItems = subData.value || [];
            // Check for nested subfolders (e.g. Policies/GL)
            for (const sub of subItems) {
              if (sub.folder) {
                const nestedRes = await fetch(
                  `${GRAPH}/drives/${driveId}/items/${sub.id}/children?$select=name,id,folder,file,webUrl,lastModifiedDateTime,size&$top=100`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (nestedRes.ok) {
                  const nestedData = await nestedRes.json();
                  const nestedFiles = (nestedData.value || []).filter(f => f.file);
                  if (nestedFiles.length > 0) {
                    result[`${item.name}/${sub.name}`] = nestedFiles;
                  }
                }
              } else if (sub.file) {
                if (!result[item.name]) result[item.name] = [];
                result[item.name].push(sub);
              }
            }
          }
        } else if (item.file) {
          if (!result['']) result[''] = [];
          result[''].push(item);
        }
      }
      setVaultDocs(result);
    } catch (err) {
      console.error('[Vault Docs] Load error:', err.message);
      setVaultDocs({});
    } finally {
      setVaultDocsLoading(false);
    }
  }, [getToken, insured.CompanyName]);

  useEffect(() => {
    if (activeTab === 'Profile') loadVaultDocs();
  }, [activeTab, loadVaultDocs]);

  async function handleVaultRename(itemId, currentName) {
    const newName = window.prompt('Rename file:', currentName);
    if (!newName || !newName.trim() || newName.trim() === currentName) return;
    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);
      const res = await fetch(
        `${GRAPH}/drives/${driveId}/items/${itemId}`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName.trim() }),
        }
      );
      if (!res.ok) throw new Error(`Rename failed (${res.status})`);
      await loadVaultDocs();
    } catch (err) {
      alert('Rename failed: ' + err.message);
    }
  }

  async function handleVaultDownload(itemId, fileName) {
    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);
      const res = await fetch(
        `${GRAPH}/drives/${driveId}/items/${itemId}/content`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  }

  // --- ACORD 25 COI state ---
  const [certHolders, setCertHolders] = useState([]);
  const [selectedCertHolderIdx, setSelectedCertHolderIdx] = useState(-1);
  const [acord25Data, setAcord25Data] = useState(null);
  const [acord25Loading, setAcord25Loading] = useState(false);
  const [acord28Data, setAcord28Data] = useState(null);
  const [acord28Loading, setAcord28Loading] = useState(false);

  // --- Policy stack sidebar for ACORDs tab ---
  const [acordPolicies, setAcordPolicies] = useState([]);
  const [acordSelectedPolicyId, setAcordSelectedPolicyId] = useState(null);
  const [acordPoliciesLoaded, setAcordPoliciesLoaded] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('fig-acord-sidebar-width');
    return saved ? Math.max(180, Math.min(400, Number(saved))) : 250;
  });
  const [acordFormDirty, setAcordFormDirty] = useState(false);
  const [acordHintView, setAcordHintView] = useState(null);

  function handlePolicySidebarClick(policyId) {
    if (policyId === acordSelectedPolicyId) return;
    if (!acordFormDirty) {
      setAcordSelectedPolicyId(policyId);
      setAcordView(null);
      return;
    }
    const choice = window.confirm('You have unsaved changes. Switch without saving?');
    if (choice) {
      setAcordFormDirty(false);
      setAcordSelectedPolicyId(policyId);
      setAcordView(null);
    }
  }

  const [collapsedLOBs, setCollapsedLOBs] = useState(() => {
    try {
      const saved = localStorage.getItem('fig-acord-collapsed-lobs');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  function toggleLOBCollapse(lob) {
    setCollapsedLOBs(prev => {
      const next = new Set(prev);
      if (next.has(lob)) next.delete(lob); else next.add(lob);
      localStorage.setItem('fig-acord-collapsed-lobs', JSON.stringify([...next]));
      return next;
    });
  }

  const policiesByLOB = useMemo(() => {
    const groups = {};
    (acordPolicies || []).forEach(p => {
      const lob = p.LOB || 'Other';
      if (!groups[lob]) groups[lob] = [];
      groups[lob].push(p);
    });
    // Current (Active/Bound) rows first, newest first; prior-term history after.
    return Object.keys(groups).sort().map(lob => ({ lob, policies: currentFirst(groups[lob]) }));
  }, [acordPolicies]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('fig-acord-sidebar-collapsed') === 'true';
  });
  const sidebarDragRef = useRef(null);

  function toggleSidebarCollapsed() {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('fig-acord-sidebar-collapsed', String(next));
      return next;
    });
  }

  function handleSidebarDragStart(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    function onMove(ev) {
      const newW = Math.max(180, Math.min(400, startW + (ev.clientX - startX)));
      setSidebarWidth(newW);
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      localStorage.setItem('fig-acord-sidebar-width', String(sidebarWidth));
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // Save width on change (debounced via the mouseup handler above, but also catch final value)
  useEffect(() => {
    localStorage.setItem('fig-acord-sidebar-width', String(sidebarWidth));
  }, [sidebarWidth]);

  const loadAcordPolicies = useCallback(async () => {
    try {
      const token = await getToken();
      const items = await getItems(token, FIG_LISTS.POLICIES);
      const mine = items.filter(p => String(p.InsuredId) === String(insured.id));
      setAcordPolicies(mine);
      if (mine.length > 0 && !acordSelectedPolicyId) {
        // Newest current row (never a prior-term Expired row when one exists).
        setAcordSelectedPolicyId(currentFirst(mine)[0].id);
      }
      setAcordPoliciesLoaded(true);
    } catch (err) {
      console.error('[ACORDs] Failed to load policies:', err.message);
    }
  }, [getToken, insured.id]);

  useEffect(() => {
    if ((activeTab === 'ACORDs' || activeTab === 'Profile' || activeTab === 'Application') && !acordPoliciesLoaded) loadAcordPolicies();
  }, [activeTab, acordPoliciesLoaded, loadAcordPolicies]);

  // --- Derived: group vault documents by policy (LOB-matched) ---
  const documentGroups = useMemo(() => {
    const policyBuckets = []; // [{ policy, files, matchedFolderKey }]
    const claimedKeys = new Set();

    // Sort policies so most recent per LOB wins the folder.
    // Priority: Active/Bound status first, then latest EffectiveDate (parsed via
    // parseLocalDate; an unparseable date sorts last, never as epoch 0).
    // Expired ranks below Active, Bound, Quoted and Submitted.
    const statusRank = { 'Active': 0, 'Bound': 1, 'Quote': 2, 'Quoted': 2, 'Submitted': 2, 'Cancelled': 3, 'Terminated': 4, 'Expired': 5 };
    const sortedPolicies = [...(acordPolicies || [])].sort((a, b) => {
      const sa = statusRank[a.Status] ?? 9;
      const sb = statusRank[b.Status] ?? 9;
      if (sa !== sb) return sa - sb;
      const da = parseLocalDate(a.EffectiveDate);
      const db = parseLocalDate(b.EffectiveDate);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.getTime() - da.getTime();
    });

    const docs = vaultDocs || {};

    for (const policy of sortedPolicies) {
      const folder = lobToVaultFolder(policy.LOB);
      const key = folder ? `Policies/${folder}` : null;
      let files = [];
      let matchedFolderKey = null;
      if (key && docs[key] && !claimedKeys.has(key)) {
        files = docs[key];
        claimedKeys.add(key);
        matchedFolderKey = key;
      }
      policyBuckets.push({ policy, files, matchedFolderKey });
    }

    // ACORD Forms — always-present group
    const acordFiles = docs['ACORDs'] || [];
    if (docs['ACORDs']) claimedKeys.add('ACORDs');

    // Unfiled — everything not claimed
    const unfiledFiles = [];
    for (const [key, files] of Object.entries(docs)) {
      if (claimedKeys.has(key)) continue;
      for (const f of files) unfiledFiles.push({ ...f, _sourceKey: key });
    }

    // --- Group policyBuckets by LOB into collapsible folders ---
    const lobMap = new Map(); // lobKey -> folder
    for (const bucket of policyBuckets) {
      const rawLob = (bucket.policy.LOB || '').trim();
      const lobLabel = rawLob || 'Unclassified';
      const lobKey = rawLob
        ? `lob-${rawLob.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`
        : 'lob-_unclassified';
      if (!lobMap.has(lobKey)) {
        lobMap.set(lobKey, {
          lobKey,
          lobLabel,
          buckets: [],
          totalFiles: 0,
          bestStatusRank: 9,
        });
      }
      const folder = lobMap.get(lobKey);
      folder.buckets.push(bucket);
      folder.totalFiles += bucket.files.length;
      const rank = statusRank[bucket.policy.Status] ?? 9;
      if (rank < folder.bestStatusRank) folder.bestStatusRank = rank;
    }

    const lobFolders = [...lobMap.values()].sort((a, b) => {
      if (a.bestStatusRank !== b.bestStatusRank) return a.bestStatusRank - b.bestStatusRank;
      return a.lobLabel.localeCompare(b.lobLabel);
    });

    return { policyBuckets, lobFolders, acordFiles, unfiledFiles };
  }, [vaultDocs, acordPolicies]);

  function fileMatchesChecklist(files, test) {
    return files.some(f => test.test(f.name || ''));
  }

  const acordSelectedPolicy = acordPolicies.find(p => p.id === acordSelectedPolicyId) || null;

  async function loadAcord28Data() {
    setAcord28Loading(true);
    try {
      const token = await getToken();
      const [policyItems, buildingItems, aiItems, covItems, ccItems] = await Promise.all([
        getItems(token, FIG_LISTS.POLICIES),
        getItems(token, FIG_LISTS.BUILDINGS),
        getItems(token, FIG_LISTS.ADDITIONAL_INTERESTS),
        getItems(token, FIG_LISTS.COVERAGES),
        getItems(token, FIG_LISTS.CLASS_CODES),
      ]);
      const iid = String(insured.id);
      const filteredAI = aiItems.filter(ai => String(ai.InsuredId) === iid);
      console.log('[loadAcord28Data] insured.id:', insured.id, 'iid:', iid);
      console.log('[loadAcord28Data] Total AI records:', aiItems.length, 'Matched:', filteredAI.length);
      if (filteredAI.length === 0 && aiItems.length > 0) {
        console.log('[loadAcord28Data] AI InsuredId samples:', aiItems.slice(0, 5).map(ai => `"${ai.InsuredId}"`));
      }
      setAcord28Data({
        policies: policyItems.filter(p => String(p.InsuredId) === iid),
        buildings: buildingItems.filter(b => String(b.InsuredId) === iid),
        additionalInterests: filteredAI,
        coverages: covItems.filter(c => String(c.InsuredId) === iid),
        classCodes: ccItems.filter(c => String(c.InsuredId) === iid),
      });
    } catch (err) {
      console.error('[ACORD 28] Load error:', err.message);
    } finally {
      setAcord28Loading(false);
    }
  }

  async function loadAcord25Data() {
    setAcord25Loading(true);
    try {
      const token = await getToken();
      const [agencyItems, policyItems, covItems, aiItems, officerItems] = await Promise.all([
        getItems(token, FIG_LISTS.AGENCY),
        getItems(token, FIG_LISTS.POLICIES),
        getItems(token, FIG_LISTS.COVERAGES),
        getItems(token, FIG_LISTS.ADDITIONAL_INTERESTS),
        getItems(token, FIG_LISTS.OFFICERS),
      ]);
      const iid = String(insured.id);
      const agency = agencyItems.length > 0 ? agencyItems[0] : null;
      const policies = policyItems.filter(p => String(p.InsuredId) === iid);
      const covs = covItems.filter(c => String(c.InsuredId) === iid);
      const officers = officerItems.filter(o => String(o.InsuredId) === iid);
      const holders = aiItems.filter(ai =>
        String(ai.InsuredId) === iid &&
        ((ai.Notes || '').toLowerCase().includes('certificate holder') ||
         (ai.InterestType || '').toLowerCase().includes('mortgagee') ||
         (ai.InterestType || '').toLowerCase().includes('loss payee') ||
         (ai.InterestType || '').toLowerCase().includes('additional insured') ||
         (ai.InterestType || '').toLowerCase().includes('certificate'))
      );
      setCertHolders(holders);
      setSelectedCertHolderIdx(holders.length > 0 ? 0 : -1);
      setAcord25Data({ agency, policies, coverages: covs, officers });
    } catch (err) {
      console.error('[ACORD 25] Load error:', err.message);
    } finally {
      setAcord25Loading(false);
    }
  }

  // --- Policies tab ---
  const [policiesList, setPoliciesList] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);

  // --- Loss Runs tab (read-only; status is computed by lossRunStatus, never stored) ---
  const [lossRunData, setLossRunData] = useState({ sources: [], policies: [], requests: [], resolutions: [], warnings: [] });
  const [lossRunLoaded, setLossRunLoaded] = useState(false);
  const [lossRunLoading, setLossRunLoading] = useState(false);
  const [lossRunError, setLossRunError] = useState(null);
  const [lossRunExpanded, setLossRunExpanded] = useState(null);   // sourceId of the open row
  const [lossRunDirOpen, setLossRunDirOpen] = useState(false);
  // Log-entry form: null, or { sourceId, sourceName, original (row | null), values, errors, saveError }
  const [lossRunForm, setLossRunForm] = useState(null);
  const [lossRunSaving, setLossRunSaving] = useState(false);

  // Loss run authorization (FIG_Insureds LossRunAuth* columns). authInsured is
  // the row as last read — the `insured` prop can be stale after a save here,
  // and loadLossRuns replaces it with the fresh row on every load.
  const [authInsured, setAuthInsured] = useState(insured);
  useEffect(() => { setAuthInsured(insured); }, [insured]);
  const [authForm, setAuthForm] = useState(null);   // null | { values, errors, saveError }
  const [authSaving, setAuthSaving] = useState(false);
  const submitAuthForm = async () => {
    if (!authForm || authSaving) return;
    const errors = validateAuthForm(authForm.values, new Date());
    if (Object.keys(errors).length) { setAuthForm(prev => ({ ...prev, errors })); return; }
    setAuthSaving(true);
    try {
      const token = await getToken();
      const result = await saveAuthorization({ token, form: authForm.values, insured: authInsured, today: new Date(), api: { updateItem } });
      if (!result.ok) { setAuthForm(prev => ({ ...prev, errors: result.errors })); return; }
      if (!result.skipped) {
        // Re-read the row so the block and the card notes reflect SharePoint, not the form.
        const rows = await getItems(token, FIG_LISTS.INSUREDS);
        const fresh = rows.find(r => String(r.id) === String(insured.id));
        if (fresh) setAuthInsured(fresh);
      }
      setAuthForm(null);
    } catch (err) {
      console.error('[Loss Runs] Authorization save failed:', err.message);
      setAuthForm(prev => prev && ({ ...prev, saveError: err.message }));
    } finally {
      setAuthSaving(false);
    }
  };

  const openLossRunForm = (line, original, prefill) => {
    setLossRunExpanded(line.sourceId);
    setLossRunForm({
      sourceId: line.sourceId, sourceName: line.name, original: original || null,
      values: original ? requestFormFromRow(original) : { ...emptyRequestForm(), ...(prefill || {}) }, errors: {}, saveError: null,
    });
  };

  // "Copy prompt": the plain-text prompt for Claude chat (utils/lossRunPrompt),
  // gated in the builder (DoNotContact -> blocked). Nothing is sent from here;
  // nothing is logged until the user saves the offered Log form.
  const [promptState, setPromptState] = useState(null);   // null | { scope, sourceId?, copied, blocked?, error? }
  // Vault path "link" on the scoreboard: copies the path to the clipboard. It
  // never tries to open SharePoint.
  const [copiedPath, setCopiedPath] = useState(null);
  const copyVaultPath = async (path) => {
    try { await navigator.clipboard.writeText(path); setCopiedPath(path); }
    catch (err) { console.warn('[Loss Runs] clipboard unavailable:', err.message); setCopiedPath(null); }
  };
  const cardRefs = useRef({});
  // Grid chip popover. Open state is ONE chip key (line|term|source) or null;
  // the popover is portaled to document.body with position: fixed and placed
  // from the chip's rect (flipping to stay in the viewport). It closes on
  // outside mousedown, Escape, and any scroll/resize, and focus returns to
  // the chip that opened it.
  const [chipPopover, setChipPopover] = useState(null);   // null | { key, chip, anchorEl }
  const popoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const closeChipPopover = useCallback((refocus = true) => {
    setChipPopover(prev => { if (prev?.anchorEl && refocus) { try { prev.anchorEl.focus(); } catch { /* detached */ } } return null; });
  }, []);
  useLayoutEffect(() => {
    if (!chipPopover?.anchorEl || !popoverRef.current) { setPopoverPos(null); return; }
    const anchor = chipPopover.anchorEl.getBoundingClientRect();
    const box = popoverRef.current.getBoundingClientRect();
    setPopoverPos(placePopover({ anchor, size: { width: box.width, height: box.height }, viewport: { width: window.innerWidth, height: window.innerHeight } }));
  }, [chipPopover]);
  useEffect(() => {
    if (!chipPopover) return undefined;
    const onDown = (ev) => { if (popoverRef.current && !popoverRef.current.contains(ev.target) && ev.target !== chipPopover.anchorEl) closeChipPopover(false); };
    const onKey = (ev) => { if (ev.key === 'Escape') closeChipPopover(true); };
    const onMove = () => closeChipPopover(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    document.addEventListener('scroll', onMove, true);   // capture: the grid wrapper's own scroll too
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('scroll', onMove, true);
    };
  }, [chipPopover, closeChipPopover]);
  // When the grid is narrower than its content, start scrolled to the newest term.
  const gridWrapRef = useRef(null);
  useEffect(() => {
    const el = gridWrapRef.current;
    if (lossRunLoaded && el && el.scrollWidth > el.clientWidth) el.scrollLeft = el.scrollWidth;
  }, [lossRunLoaded, activeTab]);
  const copyPrompt = async ({ scope, sourceId }) => {
    const text = buildClaudePrompt({ insured: authInsured, ...lossRunData, today: new Date(), scope });   // lossRunData includes resolutions
    if (typeof text !== 'string') { setPromptState({ scope, sourceId, copied: false, blocked: text.blocked }); return; }
    try {
      await navigator.clipboard.writeText(text);
      setPromptState({ scope, sourceId, copied: true });
    } catch (err) {
      console.warn('[Loss Runs] clipboard unavailable:', err.message);
      setPromptState({ scope, sourceId, copied: false, error: err.message });
    }
  };

  // "Record why it's OK": one NEW FIG_LossRunResolutions row per save; nothing is
  // edited in place and nothing is deleted. Colors never change because of it.
  const [resForm, setResForm] = useState(null);   // null | { sourceId, values, errors, saveError }
  const [resSaving, setResSaving] = useState(false);
  const openResForm = (sourceId, policyIds, season) => {
    setLossRunExpanded(sourceId);
    setResForm({ sourceId, values: emptyResolutionForm({ season, policyIds }), errors: {}, saveError: null });
  };
  const setResField = (k, v) => setResForm(prev => prev && ({ ...prev, values: { ...prev.values, [k]: v }, errors: { ...prev.errors, [k]: undefined } }));
  const submitResForm = async () => {
    if (!resForm || resSaving) return;
    const errors = validateResolutionForm(resForm.values, new Date());
    if (Object.keys(errors).length) { setResForm(prev => ({ ...prev, errors })); return; }
    setResSaving(true);
    try {
      const token = await getToken();
      const result = await saveResolution({ token, form: resForm.values, insuredId: insured.id, today: new Date(), api: { createItem } });
      if (!result.ok) { setResForm(prev => ({ ...prev, errors: result.errors })); return; }
      setResForm(null);
      await loadLossRuns();
    } catch (err) {
      console.error('[Loss Runs] Resolution save failed:', err.message);
      setResForm(prev => prev && ({ ...prev, saveError: err.message }));
    } finally {
      setResSaving(false);
    }
  };

  // Handoff PDF: model from an explicit allowlist (buildHandoffModel), rendered
  // with pdf-lib, downloaded. No vault write.
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffError, setHandoffError] = useState(null);
  const downloadHandoff = async () => {
    if (handoffBusy) return;
    setHandoffBusy(true);
    setHandoffError(null);
    try {
      let brandAssets = null;
      try { brandAssets = await getBrandAssets(getToken); } catch { /* text header fallback */ }
      const model = buildHandoffModel({ insured: authInsured, ...lossRunData, today: new Date() });
      const bytes = await generateHandoffPDF(model, { brandAssets });
      downloadPdfBytes(bytes, model.filename);
    } catch (err) {
      console.error('[Loss Runs] Handoff PDF failed:', err.message);
      setHandoffError(err.message);
    } finally {
      setHandoffBusy(false);
    }
  };
  const setLossRunField = (k, v) => setLossRunForm(prev => prev && ({
    ...prev, values: { ...prev.values, [k]: v }, errors: { ...prev.errors, [k]: undefined },
  }));
  const submitLossRunForm = async () => {
    if (!lossRunForm || lossRunSaving) return;
    const errors = validateRequestForm(lossRunForm.values, new Date());
    if (Object.keys(errors).length) { setLossRunForm(prev => ({ ...prev, errors })); return; }
    setLossRunSaving(true);
    try {
      const token = await getToken();
      const result = await saveLossRunRequest({
        token, form: lossRunForm.values, original: lossRunForm.original,
        insuredId: insured.id, sourceId: lossRunForm.sourceId, sourceName: lossRunForm.sourceName,
        today: new Date(), api: { createItem, updateItem },
      });
      if (!result.ok) { setLossRunForm(prev => ({ ...prev, errors: result.errors })); return; }
      setLossRunForm(null);
      await loadLossRuns();   // re-read; colors recompute from the new rows
    } catch (err) {
      console.error('[Loss Runs] Save failed:', err.message);
      setLossRunForm(prev => prev && ({ ...prev, saveError: err.message }));
    } finally {
      setLossRunSaving(false);
    }
  };

  // Every load re-reads all four sources of truth (sources, policies,
  // requests, the insured row). Runs on each entry to the tab and on Refresh;
  // nothing is cached between loads.
  const loadLossRuns = useCallback(async () => {
    setLossRunLoading(true);
    setLossRunError(null);
    try {
      const token = await getToken();
      const data = await loadLossRunData({ getItems, token, insuredId: insured.id });
      setLossRunData({ sources: data.sources, policies: data.policies, requests: data.requests, resolutions: data.resolutions || [], warnings: data.warnings || [] });
      if (data.insured) setAuthInsured(data.insured);
      setLossRunLoaded(true);
    } catch (err) {
      console.error('[Loss Runs] Load error:', err.message);
      setLossRunError(err.message);
    } finally {
      setLossRunLoading(false);
    }
  }, [getToken, insured.id]);

  useEffect(() => {
    if (activeTab === 'Loss Runs') loadLossRuns();
  }, [activeTab, loadLossRuns]);
  const [expandedPolicyId, setExpandedPolicyId] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);

  // --- Carrier typeahead ---
  const [carrierResults, setCarrierResults] = useState([]);
  const [carrierSearching, setCarrierSearching] = useState(false);
  const [showCarrierResults, setShowCarrierResults] = useState(false);
  const carrierTimerRef = useRef(null);

  function handleCarrierSearch(text, setFields, fields) {
    console.log('[Carrier] handleCarrierSearch called:', text);
    if (carrierTimerRef.current) clearTimeout(carrierTimerRef.current);
    setFields({ ...fields, Carrier: text });
    if (text.length < 2) { setCarrierResults([]); setShowCarrierResults(false); return; }
    carrierTimerRef.current = setTimeout(async () => {
      console.log('[Carrier] Searching for:', text);
      setCarrierSearching(true);
      try {
        const token = await getToken();
        const results = await searchCarriers(token, text);
        console.log('[Carrier] Results:', results.length, results.slice(0, 3).map(r => r.CompanyName));
        setCarrierResults(results);
        setShowCarrierResults(results.length > 0);
      } catch (err) {
        console.error('[Carrier search] Failed:', err.message);
      } finally {
        setCarrierSearching(false);
      }
    }, 300);
  }

  function selectCarrier(carrier, setFields, fields) {
    setFields({
      ...fields,
      Carrier: carrier.CompanyName || '',
      NAICCode: carrier.NAICCode || '',
      AdmittedStatus: carrier.CarrierType || '',
    });
    setShowCarrierResults(false);
    setCarrierResults([]);
  }
  const [showTriageRecords, setShowTriageRecords] = useState(false);
  const [triageRecords, setTriageRecords] = useState([]);
  const [triageLoading, setTriageLoading] = useState(false);

  async function loadTriageRecords() {
    setTriageLoading(true);
    try {
      const token = await getToken();
      const all = await getItems(token, FIG_LISTS.TRIAGE_QUEUE);
      const matches = all.filter(t =>
        t.Status === 'filed' &&
        (t.EntityName || '').toLowerCase() === (insured.CompanyName || '').toLowerCase() &&
        t.PolicyDocType &&
        !t.PolicyId
      );
      setTriageRecords(matches.sort((a, b) => new Date(b.FiledDate || 0) - new Date(a.FiledDate || 0)));
    } catch (err) {
      console.error('[Triage] Load failed:', err.message);
      setTriageRecords([]);
    } finally {
      setTriageLoading(false);
    }
  }

  const [selectedTriageRecord, setSelectedTriageRecord] = useState(null);
  const [matchingPolicies, setMatchingPolicies] = useState([]);

  function handleSelectTriageRecord(t) {
    const matches = policiesList.filter(p =>
      t.LOB && (p.LOB || '').toLowerCase() === (t.LOB || '').toLowerCase()
    );
    setSelectedTriageRecord(t);
    setMatchingPolicies(matches);
    if (matches.length === 0) {
      // No matches — auto-create
      handleCreateNewFromTriage(t);
    }
  }

  async function handleCreateNewFromTriage(t) {
    const statusMap = { 'Quote': 'Quote', 'Binder': 'Bound', 'Policy': 'Active' };
    try {
      const token = await getToken();
      const newPolicy = await createItem(token, FIG_LISTS.POLICIES, {
        Title: `${t.LOB || 'Policy'} - ${t.PolicyDocType}`,
        InsuredId: String(insured.id),
        LOB: t.LOB || '',
        EffectiveDate: t.EffectiveDate || '',
        ExpirationDate: t.ExpirationDate || '',
        Status: statusMap[t.PolicyDocType] || 'Quote',
      });
      await updateItem(token, FIG_LISTS.TRIAGE_QUEUE, t.id, { PolicyId: String(newPolicy.id) });
      setTriageRecords(prev => prev.filter(r => r.id !== t.id));
      setSelectedTriageRecord(null);
      setMatchingPolicies([]);
      await loadPolicies();
    } catch (err) {
      alert('Failed to create policy: ' + err.message);
    }
  }

  async function handleLinkToExistingPolicy(t, policy) {
    const statusMap = { 'Binder': 'Bound', 'Policy': 'Active' };
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.TRIAGE_QUEUE, t.id, { PolicyId: String(policy.id) });
      if (statusMap[t.PolicyDocType] && policy.Status !== statusMap[t.PolicyDocType]) {
        await updateItem(token, FIG_LISTS.POLICIES, policy.id, { Status: statusMap[t.PolicyDocType] });
      }
      setTriageRecords(prev => prev.filter(r => r.id !== t.id));
      setSelectedTriageRecord(null);
      setMatchingPolicies([]);
      await loadPolicies();
    } catch (err) {
      alert('Failed to link policy: ' + err.message);
    }
  }

  // Policy form constants now shared with CockpitPolicyEditor via ./policyFormOptions
  // (POLICY_LOB_OPTIONS, POLICY_STATUS_OPTIONS, INVOICE_FEES_OPTIONS, emptyPolicy
  // are imported at the top of this file).
  const [showInactive, setShowInactive] = useState(false);

  // --- Layer Participants & Endorsements for expanded policy ---
  const [participants, setParticipants] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [policyFees, setPolicyFees] = useState([]);

  // --- Collapsible sections in expanded policy ---
  const [collapsedSections, setCollapsedSections] = useState({
    policyInfo: false, financial: true, additionalFees: true, participants: true, endorsements: true,
  });
  function toggleSection(key) {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }
  function sectionHeader(label, key, extra) {
    const collapsed = collapsedSections[key];
    return (
      <div onClick={() => toggleSection(key)} style={{
        background: '#2d2d44', color: '#fff', padding: '0.35rem 0.6rem',
        fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderRadius: '3px', marginBottom: collapsed ? 0 : '0.5rem', userSelect: 'none',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.7rem' }}>&#9660;</span>
          {label}
        </span>
        {extra && <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>{extra}</span>}
      </div>
    );
  }

  const loadPolicies = useCallback(async () => {
    setPoliciesLoading(true);
    try {
      const token = await getToken();
      const items = await getItems(token, FIG_LISTS.POLICIES);
      const mine = items.filter(p => String(p.InsuredId) === String(insured.id));
      setPoliciesList(mine);
    } catch (err) {
      console.error('[Policies] Load error:', err.message);
    } finally {
      setPoliciesLoading(false);
    }
  }, [getToken, insured.id]);

  useEffect(() => {
    if (activeTab === 'Policies') loadPolicies();
  }, [activeTab, loadPolicies]);

  async function handleSavePolicy(fields, existingId) {
    setPolicySaving(true);
    try {
      const token = await getToken();
      if (existingId) {
        await updateItem(token, FIG_LISTS.POLICIES, existingId, fields);
      } else {
        await createItem(token, FIG_LISTS.POLICIES, {
          Title: `${fields.LOB || 'Policy'} - ${fields.Carrier || ''}`,
          InsuredId: String(insured.id),
          ...fields,
        });
      }
      setShowAddPolicy(false);
      setExpandedPolicyId(null);
      setEditingPolicy(null);
      await loadPolicies();
    } catch (err) {
      alert('Error saving policy: ' + err.message);
    } finally {
      setPolicySaving(false);
    }
  }

  async function handleDeletePolicy(policyId) {
    if (!window.confirm('Delete this policy? This cannot be undone.')) return;
    try {
      const token = await getToken();
      await deleteItem(token, FIG_LISTS.POLICIES, policyId);
      setExpandedPolicyId(null);
      setEditingPolicy(null);
      await loadPolicies();
    } catch (err) {
      alert('Error deleting policy: ' + err.message);
    }
  }

  async function loadPolicySubs(policyId) {
    try {
      const token = await getToken();
      const [pItems, eItems, fItems] = await Promise.all([
        getItems(token, FIG_LISTS.LAYER_PARTICIPANTS),
        getItems(token, FIG_LISTS.ENDORSEMENTS),
        getItems(token, FIG_LISTS.POLICY_FEES),
      ]);
      setParticipants(pItems.filter(p => String(p.PolicyId) === String(policyId)));
      setEndorsements(eItems.filter(e => String(e.PolicyId) === String(policyId)));
      setPolicyFees(fItems.filter(f => String(f.PolicyId) === String(policyId)));
    } catch (err) {
      console.error('[PolicySubs] Load error:', err.message);
    }
  }

  async function saveParticipant(fields, existingId, policyId) {
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.LAYER_PARTICIPANTS, existingId, fields);
    } else {
      await createItem(token, FIG_LISTS.LAYER_PARTICIPANTS, {
        Title: fields.ParticipantName || 'Participant',
        PolicyId: String(policyId), InsuredId: String(insured.id), ...fields,
      });
    }
    await loadPolicySubs(policyId);
  }

  async function deleteParticipant(id, policyId) {
    if (!window.confirm('Delete this participant?')) return;
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.LAYER_PARTICIPANTS, id);
    await loadPolicySubs(policyId);
  }

  async function saveEndorsement(fields, existingId, policyId) {
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.ENDORSEMENTS, existingId, fields);
    } else {
      await createItem(token, FIG_LISTS.ENDORSEMENTS, {
        Title: fields.EndorsementNumber || 'Endorsement',
        PolicyId: String(policyId), InsuredId: String(insured.id), ...fields,
      });
    }
    await loadPolicySubs(policyId);
  }

  async function deleteEndorsement(id, policyId) {
    if (!window.confirm('Delete this endorsement?')) return;
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.ENDORSEMENTS, id);
    await loadPolicySubs(policyId);
  }

  async function savePolicyFee(fields, existingId, policyId) {
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.POLICY_FEES, existingId, fields);
    } else {
      await createItem(token, FIG_LISTS.POLICY_FEES, {
        Title: fields.FeeLabel || 'Fee',
        PolicyId: Number(policyId), ...fields,
      });
    }
    await loadPolicySubs(policyId);
  }

  async function deletePolicyFee(id, policyId) {
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.POLICY_FEES, id);
    await loadPolicySubs(policyId);
  }

  function renderPolicyForm(fields, setFields, onSave, onCancel, isNew) {
    const f = (key) => fields[key] ?? '';
    const set = (key, val) => setFields({ ...fields, [key]: val });
    return (
      <div className="FIG-add-form">
        <div style={{ marginBottom: '0.75rem' }}>
          {sectionHeader('Policy Info', 'policyInfo')}
          {!collapsedSections.policyInfo && <div className="FIG-field-grid" style={{ padding: '0.5rem' }}>
            <label className="FIG-field">
              <span className="FIG-field-label">Line of Business</span>
              <select className="FIG-input" value={f('LOB')} onChange={e => set('LOB', e.target.value)}>
                {POLICY_LOB_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
              </select>
            </label>
            <label className="FIG-field">
              <span className="FIG-field-label">Status</span>
              <select className="FIG-input" value={f('Status')} onChange={e => set('Status', e.target.value)}>
                {POLICY_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
              </select>
            </label>
            <div className="FIG-field" style={{ position: 'relative' }}>
              <span className="FIG-field-label">Carrier</span>
              <input className="FIG-input" value={f('Carrier')}
                onChange={e => handleCarrierSearch(e.target.value, setFields, fields)}
                onFocus={() => { if (carrierResults.length > 0) setShowCarrierResults(true); }}
                onBlur={() => setTimeout(() => setShowCarrierResults(false), 200)}
                placeholder="Type to search carriers..."
                autoComplete="off" />
              {carrierSearching && <span style={{ position: 'absolute', right: '8px', top: '22px', fontSize: '0.7rem', color: '#888' }}>...</span>}
              {showCarrierResults && carrierResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                  background: '#fff', border: '1px solid #ccc', borderRadius: '0 0 6px 6px',
                  maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {carrierResults.map(c => (
                    <div key={c.id} style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                      fontSize: '0.82rem' }}
                      onMouseDown={() => selectCarrier(c, setFields, fields)}>
                      <div style={{ fontWeight: 600, color: '#333' }}>
                        {c.CompanyName}
                        {c.IsMyCarrier && <span style={{ marginLeft: '6px', fontSize: '0.65rem', background: '#4B0082',
                          color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>MY CARRIER</span>}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#888' }}>
                        NAIC: {c.NAICCode || '—'} | {c.CarrierType || ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label className="FIG-field"><span className="FIG-field-label">NAIC Code</span>
              <input className="FIG-input" value={f('NAICCode')} onChange={e => set('NAICCode', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Policy Number</span>
              <input className="FIG-input" value={f('PolicyNumber')} onChange={e => set('PolicyNumber', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Reference Number</span>
              <input className="FIG-input" value={f('ReferenceNumber')} onChange={e => set('ReferenceNumber', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Quote Number</span>
              <input className="FIG-input" value={f('QuoteNumber')} onChange={e => set('QuoteNumber', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Effective Date</span>
              <input className="FIG-input" value={f('EffectiveDate')} onChange={e => set('EffectiveDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
            <label className="FIG-field"><span className="FIG-field-label">Expiration Date</span>
              <input className="FIG-input" value={f('ExpirationDate')} onChange={e => set('ExpirationDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
            <label className="FIG-field"><span className="FIG-field-label">Layer</span>
              <input className="FIG-input" value={f('Layer')} onChange={e => set('Layer', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Admitted Status</span>
              <input className="FIG-input" value={f('AdmittedStatus')} onChange={e => set('AdmittedStatus', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Broker Contact</span>
              <input className="FIG-input" value={f('BrokerContact')} onChange={e => set('BrokerContact', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Program Name</span>
              <input className="FIG-input" value={f('ProgramName')} onChange={e => set('ProgramName', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Program Code</span>
              <input className="FIG-input" value={f('ProgramCode')} onChange={e => set('ProgramCode', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Underwriter</span>
              <input className="FIG-input" value={f('Underwriter')} onChange={e => set('Underwriter', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Underwriter Office</span>
              <input className="FIG-input" value={f('UnderwriterOffice')} onChange={e => set('UnderwriterOffice', e.target.value)} /></label>
            <label className="FIG-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" checked={fields.AgencyBilled === true} onChange={e => set('AgencyBilled', e.target.checked)} />
              <span className="FIG-field-label" style={{ margin: 0 }}>Agency Billed</span>
            </label>
            {(f('Status') === 'Cancelled' || f('Status') === 'Terminated') && <>
              <label className="FIG-field"><span className="FIG-field-label">Cancellation Effective Date</span>
                <input className="FIG-input" value={f('CancellationEffectiveDate')} onChange={e => set('CancellationEffectiveDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
              <label className="FIG-field"><span className="FIG-field-label">Cancellation Reason</span>
                <input className="FIG-input" value={f('CancellationReason')} onChange={e => set('CancellationReason', e.target.value)} /></label>
            </>}
          </div>}
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          {sectionHeader('Financial', 'financial')}
          {!collapsedSections.financial && <div className="FIG-field-grid" style={{ padding: '0.5rem' }}>
            <label className="FIG-field"><span className="FIG-field-label">$ Premium</span>
              <input className="FIG-input" type="number" value={f('Premium')} onChange={e => set('Premium', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ State Tax</span>
              <input className="FIG-input" type="number" value={f('StateTax')} onChange={e => set('StateTax', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Stamping Fee</span>
              <input className="FIG-input" type="number" value={f('StampingFee')} onChange={e => set('StampingFee', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Inspection Fee</span>
              <input className="FIG-input" type="number" value={f('InspectionFee')} onChange={e => set('InspectionFee', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Broker Fee</span>
              <input className="FIG-input" type="number" value={f('BrokerFee')} onChange={e => set('BrokerFee', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Policy Fee</span>
              <input className="FIG-input" type="number" value={f('PolicyFee')} onChange={e => set('PolicyFee', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Agency Fee</span>
              <input className="FIG-input" type="number" value={f('AgencyFee')} onChange={e => set('AgencyFee', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Agency Fee %</span>
              <input className="FIG-input" type="number" value={f('AgencyFeePercent')} onChange={e => set('AgencyFeePercent', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Commission %</span>
              <input className="FIG-input" type="number" value={f('CommissionPercent')} onChange={e => set('CommissionPercent', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">$ Commission Amount</span>
              <input className="FIG-input" type="number" value={f('CommissionAmount')} onChange={e => set('CommissionAmount', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Wholesale Broker</span>
              <input className="FIG-input" value={f('WholesaleBroker')} onChange={e => set('WholesaleBroker', e.target.value)} /></label>
            <label className="FIG-field"><span className="FIG-field-label">Invoice Fees Display</span>
              <select className="FIG-input" value={f('InvoiceFeesDisplay')} onChange={e => set('InvoiceFeesDisplay', e.target.value)}>
                {INVOICE_FEES_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
              </select>
            </label>
          </div>}
        </div>

        {!isNew && <div style={{ marginBottom: '0.75rem' }}>
          {sectionHeader('Additional Fees', 'additionalFees')}
          {!collapsedSections.additionalFees && <div style={{ padding: '0.5rem' }}>
            {policyFees.length === 0 && <p className="FIG-empty" style={{ margin: '0.5rem 0', textAlign: 'left' }}>No additional fees.</p>}
            {policyFees.map((fee) => (
              <div key={fee.id} className="FIG-add-form" style={{ marginBottom: '0.5rem', padding: '0.5rem' }}>
                <div className="FIG-field-grid">
                  <label className="FIG-field"><span className="FIG-field-label">Label</span>
                    <input className="FIG-input" defaultValue={fee.FeeLabel || ''} onBlur={e => savePolicyFee({ ...fee, FeeLabel: e.target.value }, fee.id, expandedPolicyId)} /></label>
                  <label className="FIG-field"><span className="FIG-field-label">$ Amount</span>
                    <input className="FIG-input" type="number" defaultValue={fee.Amount ?? ''} onBlur={e => savePolicyFee({ ...fee, Amount: e.target.value }, fee.id, expandedPolicyId)} /></label>
                  <label className="FIG-field"><span className="FIG-field-label">Notes</span>
                    <input className="FIG-input" defaultValue={fee.Notes || ''} onBlur={e => savePolicyFee({ ...fee, Notes: e.target.value }, fee.id, expandedPolicyId)} /></label>
                </div>
                <button className="FIG-back-btn" style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#8B0000' }}
                  onClick={() => deletePolicyFee(fee.id, expandedPolicyId)}>Remove</button>
              </div>
            ))}
            <button className="FIG-back-btn" style={{ fontSize: '0.8rem' }}
              onClick={() => savePolicyFee({ FeeLabel: '', Amount: '', Notes: '' }, null, expandedPolicyId)}>
              + Add Fee
            </button>
          </div>}
        </div>}

        <div className="FIG-profile-actions" style={{ marginTop: '0.5rem' }}>
          <button className="FIG-save-btn" onClick={() => onSave(fields)} disabled={policySaving}>
            {policySaving ? 'Saving...' : (isNew ? 'Create Policy' : 'Update Policy')}
          </button>
          <button className="FIG-back-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    );
  }

  function handleChange(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.INSUREDS, insured.id, form);
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={activeTab === 'Loss Runs' ? 'FIG FIG--wide' : 'FIG'}>
      <div className="FIG-top-bar">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back</button>
        <h1 className="FIG-title">{insured.CompanyName || 'Insured'}</h1>
      </div>

      <div className="FIG-tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            className={'FIG-tab' + (activeTab === tab ? ' FIG-tab--active' : '')}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'Profile' ? (
        <div className="FIG-profile">
          {PROFILE_SECTIONS.map(section => (
            <fieldset className={'FIG-section' + (!profileCollapsed[section.label] ? ' FIG-section--expanded' : '')} key={section.label}>
              <legend className="FIG-section-legend" onClick={() => toggleProfileSection(section.label)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
                <span style={{ display: 'inline-block', transition: 'transform 0.2s',
                  transform: profileCollapsed[section.label] ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.55rem' }}>&#9660;</span>
                {section.label}
              </legend>
              {!profileCollapsed[section.label] && (
              <div className="FIG-field-grid">
                {section.fields.map(f => (
                  <label className={'FIG-field' + (f.textarea ? ' FIG-field--wide' : '')} key={f.key}>
                    <span className="FIG-field-label">{f.label}</span>
                    {f.textarea ? (
                      <textarea
                        className="FIG-input"
                        rows={3}
                        value={form[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                      />
                    ) : f.options ? (
                      <select
                        className="FIG-input"
                        value={form[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                      >
                        {f.options.map(opt => (
                          <option key={opt} value={opt}>{opt || '\u2014'}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="FIG-input"
                        type={f.type || 'text'}
                        value={form[f.key]}
                        onChange={e => handleChange(f.key, e.target.value)}
                        readOnly={f.readOnly || false}
                        style={f.readOnly ? { background: '#eee', color: '#888' } : undefined}
                      />
                    )}
                  </label>
                ))}
              </div>
              )}
            </fieldset>
          ))}

          <div className="FIG-profile-actions">
            <button className="FIG-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            {saveMsg && <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>{saveMsg}</span>}
          </div>

          {/* Documents from FIGVault */}
          <fieldset className="FIG-section FIG-section-documents" style={{ marginTop: 0 }}>
            <legend className="FIG-section-legend" onClick={() => toggleProfileSection('Documents')}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none' }}>
              <span style={{ display: 'inline-block', transition: 'transform 0.2s',
                transform: profileCollapsed['Documents'] ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.55rem' }}>&#9660;</span>
              Documents
              <button
                onClick={(e) => { e.stopPropagation(); setFolioOpen(true); }}
                disabled={folioOpen || !acordPoliciesLoaded || acordPolicies.length === 0}
                style={{ marginLeft: '10px', background: '#4B0082', color: '#fff',
                  border: 'none', borderRadius: '4px', padding: '3px 8px',
                  fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex',
                  alignItems: 'center', gap: '4px', opacity: (!acordPoliciesLoaded || acordPolicies.length === 0) ? 0.5 : 1 }}
                title="Export Policy Folio">
                <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>&#8615;</span>
                Export Policy Folio
              </button>
            </legend>
            {!profileCollapsed['Documents'] && <>
            {vaultDocsLoading && <p className="FIG-loading" style={{ margin: '0.5rem 0' }}>Loading documents...</p>}
            {!vaultDocsLoading && vaultDocs && documentGroups.lobFolders.length === 0
              && documentGroups.acordFiles.length === 0 && documentGroups.unfiledFiles.length === 0 && (
              <p className="FIG-empty" style={{ margin: '0.5rem 0', textAlign: 'left' }}>No documents filed yet.</p>
            )}
            {!vaultDocsLoading && vaultDocs && (() => {
              const renderFileRow = (f) => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.3rem 0.4rem', borderBottom: '1px solid #f0ebe0', fontSize: '0.82rem' }}>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                    {f.name}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#888', flexShrink: 0 }}>
                    {f.lastModifiedDateTime ? new Date(f.lastModifiedDateTime).toLocaleDateString() : ''}
                  </span>
                  {f.webUrl && (
                    <button onClick={() => window.open(f.webUrl, '_blank')}
                      style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
                        cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem', color: '#4B0082',
                        lineHeight: 1 }}
                      title="Open in SharePoint">&#128065;</button>
                  )}
                  <button onClick={() => handleVaultRename(f.id, f.name)}
                    style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
                      cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem', color: '#4B0082',
                      lineHeight: 1 }}
                    title="Rename">&#9998;</button>
                  <button onClick={() => handleVaultDownload(f.id, f.name)}
                    style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
                      cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem', color: '#4B0082',
                      lineHeight: 1 }}
                    title="Download">&#8615;</button>
                </div>
              );

              const renderGroup = (groupKey, header, files, checklist) => {
                const collapsed = policyGroupCollapsed[groupKey];
                return (
                  <div key={groupKey} style={{ marginBottom: '0.75rem', border: '1px solid #e5dfd0',
                    borderRadius: '6px', background: '#fdfcf7' }}>
                    <div onClick={() => setPolicyGroupCollapsed(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                      style={{ cursor: 'pointer', padding: '0.5rem 0.6rem', userSelect: 'none',
                        borderBottom: collapsed ? 'none' : '1px solid #eee7d4' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-block', transition: 'transform 0.2s',
                          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.55rem' }}>&#9660;</span>
                        {header}
                        <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: '#888' }}>
                          {files.length} file{files.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {checklist && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '0.4rem', marginLeft: '14px' }}>
                          {POLICY_DOC_CHECKLIST.map(c => {
                            const on = fileMatchesChecklist(files, c.test);
                            return (
                              <span key={c.label} style={{
                                fontSize: '0.65rem', padding: '2px 6px', borderRadius: '10px',
                                background: on ? '#e6f4ea' : '#f2f2f2',
                                color: on ? '#1e7a3a' : '#999',
                                border: `1px solid ${on ? '#b6dfc2' : '#e0e0e0'}`,
                              }}>
                                {on ? '✓ ' : '○ '}{c.label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {!collapsed && (
                      <div style={{ padding: '0.25rem 0.6rem 0.4rem' }}>
                        {files.length === 0
                          ? <p className="FIG-empty" style={{ margin: '0.4rem 0', textAlign: 'left', fontSize: '0.78rem' }}>No documents filed.</p>
                          : files.map(renderFileRow)}
                      </div>
                    )}
                  </div>
                );
              };

              const FolderIcon = ({ open }) => (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="#4B0082" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0 }}>
                  {open
                    ? <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1H5l-2 10V7z" />
                    : <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />}
                </svg>
              );

              const renderPolicyCard = ({ policy, files }) => {
                const header = (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.85rem' }}>
                        {policy.Carrier || policy.LOB || 'Policy'}
                      </span>
                      {policy.Status && (
                        <span className={`FIG-card-status FIG-card-status--${String(policy.Status).toLowerCase()}`}
                          style={{ fontSize: '0.65rem' }}>
                          {policy.Status}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#777' }}>
                      {policy.PolicyNumber || 'Pending'}
                      {policy.EffectiveDate ? ` | ${policy.EffectiveDate}` : ''}
                      {policy.ExpirationDate ? ` → ${policy.ExpirationDate}` : ''}
                    </div>
                  </div>
                );
                return renderGroup(`policy-${policy.id}`, header, files, true);
              };

              const renderLobFolder = (folder) => {
                const { lobKey, lobLabel, buckets, totalFiles } = folder;
                // LOB folders default to collapsed: undefined means collapsed,
                // only an explicit `false` means the user opened it.
                const collapsed = policyGroupCollapsed[lobKey] !== false;
                const policyCount = buckets.length;
                // Prior-term (Expired) buckets sit behind a per-folder history
                // toggle instead of one card each. Closed unless explicitly opened.
                const isExpiredBucket = b => (b.policy.Status || '').toLowerCase() === 'expired';
                const liveBuckets = buckets.filter(b => !isExpiredBucket(b));
                const priorBuckets = buckets.filter(isExpiredBucket);
                const historyKey = `${lobKey}:history`;
                const historyOpen = policyGroupCollapsed[historyKey] === true;
                return (
                  <div key={lobKey} style={{ marginBottom: '0.6rem', border: '1px solid #d8d1bf',
                    borderRadius: '6px', background: '#faf7ee' }}>
                    <div onClick={() => setPolicyGroupCollapsed(prev => ({
                      ...prev, [lobKey]: prev[lobKey] === false ? true : false,
                    }))}
                      style={{ cursor: 'pointer', padding: '0.55rem 0.7rem', userSelect: 'none',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        borderBottom: collapsed ? 'none' : '1px solid #eee7d4' }}>
                      <FolderIcon open={!collapsed} />
                      <span style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.9rem' }}>
                        {lobLabel}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#888' }}>
                        {policyCount} polic{policyCount === 1 ? 'y' : 'ies'} · {totalFiles} file{totalFiles === 1 ? '' : 's'}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'inline-block', transition: 'transform 0.2s',
                        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', fontSize: '0.55rem', color: '#888' }}>
                        &#9660;
                      </span>
                    </div>
                    {!collapsed && (
                      <div style={{ padding: '0.5rem 0.6rem 0.35rem' }}>
                        {liveBuckets.map(renderPolicyCard)}
                        {priorBuckets.length > 0 && (
                          <>
                            <div onClick={() => setPolicyGroupCollapsed(prev => ({ ...prev, [historyKey]: !historyOpen }))}
                              style={{ cursor: 'pointer', userSelect: 'none', fontSize: '0.75rem', color: '#888',
                                padding: '0.25rem 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'inline-block', transition: 'transform 0.2s',
                                transform: historyOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: '0.55rem' }}>&#9660;</span>
                              {priorBuckets.length} prior term{priorBuckets.length === 1 ? '' : 's'}
                            </div>
                            {historyOpen && priorBuckets.map(renderPolicyCard)}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <>
                  {documentGroups.lobFolders.map(renderLobFolder)}

                  {renderGroup(
                    '_acord',
                    <span style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.85rem' }}>ACORD Forms</span>,
                    documentGroups.acordFiles,
                    false
                  )}

                  {documentGroups.unfiledFiles.length > 0 && renderGroup(
                    '_unfiled',
                    <span style={{ fontWeight: 700, color: '#8a6d00', fontSize: '0.85rem' }}>Unfiled / Other</span>,
                    documentGroups.unfiledFiles,
                    false
                  )}
                </>
              );
            })()}
            </>}
          </fieldset>

          {folioOpen && (
            <FIGFolioExport
              insured={insured}
              policies={acordPolicies}
              getToken={getToken}
              onClose={() => setFolioOpen(false)}
            />
          )}
        </div>
      ) : activeTab === 'ACORDs' ? (
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Policy Stack Sidebar */}
          {sidebarCollapsed ? (
            /* Collapsed sidebar — thin strip */
            <div style={{ width: '30px', flexShrink: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', paddingTop: '0.25rem', gap: '0.5rem' }}>
              <button onClick={toggleSidebarCollapsed}
                style={{ background: 'none', border: '1px solid #e0d6c8', borderRadius: '4px',
                  width: '26px', height: '26px', cursor: 'pointer', fontSize: '0.7rem',
                  color: '#4B0082', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Expand policy stack">&rsaquo;</button>
              {acordSelectedPolicy && (
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed',
                  fontSize: '0.65rem', fontWeight: 700, color: '#4B0082', letterSpacing: '1px',
                  whiteSpace: 'nowrap', overflow: 'hidden', maxHeight: '120px' }}>
                  {acordSelectedPolicy.LOB || 'Policy'}
                </div>
              )}
            </div>
          ) : (
            /* Expanded sidebar */
            <div style={{ width: `${sidebarWidth}px`, flexShrink: 0, overflowY: 'auto', paddingRight: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4B0082', textTransform: 'uppercase',
                  letterSpacing: '0.5px' }}>Policy Stack</span>
                <button onClick={toggleSidebarCollapsed}
                  style={{ background: 'none', border: '1px solid #e0d6c8', borderRadius: '4px',
                    width: '22px', height: '22px', cursor: 'pointer', fontSize: '0.65rem',
                    color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Collapse">&lsaquo;</button>
              </div>
              {acordPolicies.length === 0 && (
                <div style={{ fontSize: '0.82rem', color: '#888', padding: '1rem 0' }}>
                  <p>No policies yet.</p>
                  <button className="FIG-back-btn" onClick={() => setActiveTab('Policies')} style={{ marginTop: '0.5rem' }}>
                    Go to Policies tab
                  </button>
                </div>
              )}
              {policiesByLOB.map(({ lob, policies }) => {
                const isOpen = !collapsedLOBs.has(lob);
                return (
                  <div key={lob} style={{ marginBottom: '0.35rem' }}>
                    {/* LOB folder header */}
                    <button onClick={() => toggleLOBCollapse(lob)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', width: '100%',
                        padding: '0.3rem 0.4rem', background: '#f5f0e8', border: '1px solid #e0d6c8',
                        borderRadius: '5px', cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                      <span style={{ fontSize: '0.65rem', color: '#4B0082', width: '10px', textAlign: 'center',
                        transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9654;</span>
                      <span style={{ fontWeight: 700, fontSize: '0.75rem', color: '#4B0082', flex: 1, textAlign: 'left' }}>
                        {lob}
                      </span>
                      <span style={{ fontSize: '0.6rem', color: '#888', fontWeight: 600 }}>({policies.length})</span>
                    </button>
                    {/* Nested policy cards */}
                    {isOpen && policies.map(p => {
                      const isSelected = p.id === acordSelectedPolicyId;
                      const st = (p.Status || '').toLowerCase();
                      const statusColor = st === 'active' ? '#2e7d32' : st === 'bound' ? '#e6a817'
                        : st === 'quote' ? '#1565c0' : st === 'cancelled' ? '#c0392b' : '#888';
                      return (
                        <button key={p.id} onClick={() => handlePolicySidebarClick(p.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                            textAlign: 'left', padding: '0.3rem 0.4rem 0.3rem 1.2rem',
                            marginTop: '2px', borderRadius: '5px', cursor: 'pointer', fontFamily: 'inherit',
                            border: isSelected ? '2px solid #B8860B' : '1px solid transparent',
                            background: isSelected ? '#fdf6e3' : 'transparent',
                            opacity: isSelected ? 1 : 0.75,
                            transition: 'all 0.15s',
                          }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <span style={{ fontWeight: 600, fontSize: '0.72rem', color: isSelected ? '#4B0082' : '#555',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {p.Carrier || 'No Carrier'}
                              </span>
                              <span style={{ fontSize: '0.55rem', fontWeight: 600, padding: '0px 4px', borderRadius: '3px',
                                background: statusColor + '20', color: statusColor, flexShrink: 0 }}>
                                {p.Status || '—'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#999', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.PolicyNumber || 'Pending'}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Resize Handle — only when expanded */}
          {!sidebarCollapsed && (
            <div onMouseDown={handleSidebarDragStart}
              style={{ width: '3px', cursor: 'col-resize', background: '#e0d6c8', flexShrink: 0,
                borderRadius: '2px', margin: '0 2px', minHeight: '100px',
                transition: 'background 0.15s' }}
              onMouseEnter={e => e.target.style.background = '#B8860B'}
              onMouseLeave={e => e.target.style.background = '#e0d6c8'} />
          )}

          {/* ACORD Form Area */}
          <div style={{ flex: 1, minWidth: 0, paddingLeft: '0.5rem' }}>
        {acordPolicies.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '4rem 2rem', color: '#888' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>No policies found</div>
            <div style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>Create a policy first to generate ACORD forms.</div>
            <button className="FIG-save-btn" onClick={() => setActiveTab('Policies')}>Go to Policies tab</button>
          </div>
        ) : !acordSelectedPolicyId ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '4rem 2rem', color: '#999', fontSize: '1rem', fontStyle: 'italic' }}>
            Select a policy to begin
          </div>
        ) : acordView === '125' ? (
          <ACORD125Page1 insured={insured} getToken={getToken} onBack={() => setAcordView(null)} />
        ) : acordView === '140' ? (
          acord28Loading ? (
            <p className="FIG-loading">Loading ACORD 140...</p>
          ) : acord28Data ? (
            <ACORD140
              insured={insured}
              policy={acordSelectedPolicy}
              policies={acord28Data.policies}
              buildings={acord28Data.buildings}
              additionalInterests={acord28Data.additionalInterests}
              getToken={getToken}
              onDirtyChange={setAcordFormDirty}
              onBack={() => { setAcordFormDirty(false); setAcordView(null); }}
            />
          ) : null
        ) : acordView === '130' ? (
          acord28Loading ? (
            <p className="FIG-loading">Loading ACORD 130...</p>
          ) : acord28Data ? (
            <ACORD130
              insured={insured}
              policy={acordSelectedPolicy}
              buildings={acord28Data.buildings}
              additionalInterests={acord28Data.additionalInterests}
              getToken={getToken}
              onDirtyChange={setAcordFormDirty}
              onBack={() => { setAcordFormDirty(false); setAcordView(null); }}
            />
          ) : null
        ) : acordView === '126' ? (
          acord28Loading ? (
            <p className="FIG-loading">Loading ACORD 126...</p>
          ) : acord28Data ? (
            <ACORD126
              insured={insured}
              policy={acordSelectedPolicy}
              buildings={acord28Data.buildings}
              additionalInterests={acord28Data.additionalInterests}
              classCodes={acord28Data.classCodes || []}
              getToken={getToken}
              onDirtyChange={setAcordFormDirty}
              onBack={() => { setAcordFormDirty(false); setAcordView(null); }}
            />
          ) : null
        ) : acordView === '28' ? (
          acord28Loading ? (
            <p className="FIG-loading">Loading ACORD 28...</p>
          ) : acord28Data ? (
            <ACORD28
              insured={insured}
              policies={acord28Data.policies}
              buildings={acord28Data.buildings}
              additionalInterests={acord28Data.additionalInterests}
              coverages={acord28Data.coverages}
              getToken={getToken}
              selectedPolicyId={acordSelectedPolicyId}
              onDirtyChange={setAcordFormDirty}
              onBack={() => { setAcordFormDirty(false); setAcordView(null); setAcord28Data(null); }}
            />
          ) : null
        ) : acordView === '25' ? (
          <div className="FIG-profile">
            <div className="FIG-profile-actions">
              <button className="FIG-back-btn" onClick={() => { setAcordView(null); setAcord25Data(null); }}>&lsaquo; Back to ACORDs</button>
              <h2 style={{ margin: 0, fontSize: '1rem', color: '#4B0082' }}>ACORD 25 — Certificate of Liability Insurance</h2>
            </div>
            {acord25Loading && <p className="FIG-loading">Loading data...</p>}
            {!acord25Loading && acord25Data && (
              <>
                <fieldset className="FIG-section" style={{ marginTop: '0.75rem' }}>
                  <legend className="FIG-section-legend">Certificate Holder</legend>
                  {certHolders.length === 0 ? (
                    <p className="FIG-empty">No certificate holders found. Add one in the Additional Interests tab.</p>
                  ) : (
                    <>
                      <select className="FIG-input" value={selectedCertHolderIdx}
                        onChange={e => setSelectedCertHolderIdx(Number(e.target.value))}>
                        {certHolders.map((ch, i) => (
                          <option key={ch.id || i} value={i}>
                            {ch.EntityName || 'Unnamed'} — {ch.InterestType || ''} {ch.Address ? `(${ch.City}, ${ch.State})` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedCertHolderIdx >= 0 && certHolders[selectedCertHolderIdx] && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#555', lineHeight: 1.6 }}>
                          <div><strong>{certHolders[selectedCertHolderIdx].EntityName}</strong></div>
                          <div>{certHolders[selectedCertHolderIdx].Address}</div>
                          <div>{certHolders[selectedCertHolderIdx].City}, {certHolders[selectedCertHolderIdx].State} {certHolders[selectedCertHolderIdx].Zip}</div>
                          {certHolders[selectedCertHolderIdx].ReferenceNumber && <div>Ref: {certHolders[selectedCertHolderIdx].ReferenceNumber}</div>}
                          {certHolders[selectedCertHolderIdx].Notes && <div style={{ fontStyle: 'italic', marginTop: '0.25rem' }}>{certHolders[selectedCertHolderIdx].Notes}</div>}
                        </div>
                      )}
                    </>
                  )}
                </fieldset>
                {selectedCertHolderIdx >= 0 && certHolders[selectedCertHolderIdx] && (
                  <div className="FIG-profile-actions" style={{ marginTop: '0.75rem' }}>
                    <button className="FIG-save-btn" style={{ background: '#8B0000' }} disabled={generatingCoi} onClick={async () => {
                      setGeneratingCoi(true);
                      try {
                        const ch = certHolders[selectedCertHolderIdx];
                        const notes = (ch.Notes || '').toLowerCase();
                        await generateACORD25({
                          insured,
                          agency: acord25Data.agency,
                          certHolder: {
                            FullName: ch.EntityName || '',
                            Address: ch.Address || '',
                            City: ch.City || '',
                            State: ch.State || '',
                            Zip: ch.Zip || '',
                            IsAdditionalInsured: notes.includes('additional insured'),
                            WaiverOfSubrogation: notes.includes('wos') || notes.includes('waiver'),
                            AI_LOBs: ch.Notes || '',
                            WOS_LOBs: notes.includes('wos: ') ? ch.Notes.split(/wos:\s*/i)[1]?.split('.')[0] || '' : '',
                            DescriptionLanguage: ch.Notes || '',
                          },
                          policies: acord25Data.policies,
                          coverages: acord25Data.coverages,
                          officers: acord25Data.officers,
                          getToken,
                        });
                      } catch (err) {
                        console.error('[ACORD 25] Generation failed:', err);
                        alert('Error generating COI: ' + err.message);
                      } finally {
                        setGeneratingCoi(false);
                      }
                    }}>
                      {generatingCoi ? 'Generating...' : 'Generate COI'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="FIG-acord-list">
            {(() => {
              const lob = (acordSelectedPolicy?.LOB || '').toLowerCase();
              const lobKey = Object.keys(ACORD_LOB_MAP).find(k => lob.includes(k.toLowerCase()));
              const lobFormKeys = lobKey ? ACORD_LOB_MAP[lobKey] : [];
              const activeKeys = new Set([...lobFormKeys, ...UNIVERSAL_FORM_KEYS]);

              const formToLOB = {};
              for (const [lobName, keys] of Object.entries(ACORD_LOB_MAP)) {
                for (const k of keys) formToLOB[k] = lobName;
              }

              return Object.values(ACORD_FORMS).map(f => {
                const isActive = activeKeys.has(f.view);
                const neededLOB = formToLOB[f.view];

                return (
                  <div key={f.view} className={'FIG-acord-card' + (!isActive ? ' FIG-acord-card--disabled' : '')}
                    style={{
                      cursor: 'pointer',
                      background: isActive ? '#fdf8e8' : '#fff',
                      opacity: isActive ? 1 : 0.55,
                      borderLeftColor: isActive ? '#B8860B' : '#e0d6c8',
                    }}
                    onClick={isActive ? () => {
                      setAcordHintView(null);
                      if (['28','126','140','130'].includes(f.view)) { setAcordView(f.view); loadAcord28Data(); }
                      else if (f.view === '25') { setAcordView('25'); loadAcord25Data(); }
                      else setAcordView(f.view);
                    } : () => {
                      setAcordHintView(f.view);
                      setTimeout(() => setAcordHintView(prev => prev === f.view ? null : prev), 4000);
                    }}>
                    <div style={{ flex: 1 }}>
                      <span className="FIG-acord-card-name">{f.name}</span>
                      <span className="FIG-acord-card-desc">&mdash; {f.desc}</span>
                      {acordHintView === f.view && neededLOB && (
                        <div style={{ fontSize: '0.72rem', color: '#8B0000', marginTop: '3px',
                          animation: 'triageFadeIn 0.2s ease' }}>
                          Select a {neededLOB} policy from the sidebar, or create one from the Policies tab
                        </div>
                      )}
                    </div>
                    {f.blank && (
                      <button onClick={(e) => { e.stopPropagation(); window.open(f.blank, '_blank'); }}
                        style={{ background: 'none', border: '1px solid #ccc', borderRadius: '4px',
                          color: '#4B0082', fontSize: '0.68rem', padding: '2px 6px', cursor: 'pointer',
                          flexShrink: 0, whiteSpace: 'nowrap' }}
                        title="Open blank PDF">
                        Print Blank
                      </button>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
          </div>
        </div>
      ) : activeTab === 'Policies' ? (
        <div className="FIG-profile">
          <div className="FIG-profile-actions" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="FIG-add-btn" onClick={() => {
                if (!showAddPolicy) { setEditingPolicy({ ...emptyPolicy }); setExpandedPolicyId(null); setShowTriageRecords(false); }
                else { setEditingPolicy(null); }
                setShowAddPolicy(!showAddPolicy);
              }}>
                {showAddPolicy ? 'Cancel' : '+ Add Policy'}
              </button>
              <button className="FIG-add-btn" style={{ background: '#4B0082' }} onClick={() => {
                setShowTriageRecords(!showTriageRecords);
                setShowAddPolicy(false);
                if (!showTriageRecords) loadTriageRecords();
              }}>
                {showTriageRecords ? 'Cancel' : 'Create from Triage'}
              </button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
          </div>

          {showAddPolicy && editingPolicy && renderPolicyForm(
            editingPolicy,
            setEditingPolicy,
            (fields) => handleSavePolicy(fields, null),
            () => { setShowAddPolicy(false); setEditingPolicy(null); },
            true
          )}

          {showTriageRecords && (
            <div className="FIG-add-form" style={{ gap: '0.4rem' }}>
              {!selectedTriageRecord ? (
                <>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4B0082', marginBottom: '0.25rem' }}>
                    Filed documents without a policy record:
                  </div>
                  {triageLoading && <p className="FIG-loading">Loading triage records...</p>}
                  {!triageLoading && triageRecords.length === 0 && (
                    <p className="FIG-empty" style={{ margin: '0.5rem 0', textAlign: 'left' }}>No unlinked triage records for this insured.</p>
                  )}
                  {!triageLoading && triageRecords.map(t => (
                    <button key={t.id} className="FIG-card" style={{ padding: '0.5rem 0.75rem' }}
                      onClick={() => handleSelectTriageRecord(t)}>
                      <div className="FIG-card-header">
                        <span className="FIG-card-name" style={{ fontSize: '0.85rem' }}>{t.Title || 'Document'}</span>
                        <span className={`FIG-card-status FIG-card-status--${(t.PolicyDocType || '').toLowerCase()}`}>
                          {t.PolicyDocType || ''}
                        </span>
                      </div>
                      <span className="FIG-card-contact" style={{ fontSize: '0.78rem' }}>
                        {[t.LOB, t.EffectiveDate && t.ExpirationDate ? `${t.EffectiveDate} to ${t.ExpirationDate}` : t.EffectiveDate].filter(Boolean).join(' — ')}
                      </span>
                    </button>
                  ))}
                </>
              ) : (
                <>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4B0082', marginBottom: '0.25rem' }}>
                    Link "{selectedTriageRecord.Title}" ({selectedTriageRecord.PolicyDocType})
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.5rem' }}>
                    {selectedTriageRecord.LOB}{selectedTriageRecord.EffectiveDate ? ` — ${selectedTriageRecord.EffectiveDate}` : ''}{selectedTriageRecord.ExpirationDate ? ` to ${selectedTriageRecord.ExpirationDate}` : ''}
                  </div>

                  {matchingPolicies.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>
                        Link to existing {selectedTriageRecord.LOB} policy:
                      </div>
                      {matchingPolicies.map(p => (
                        <button key={p.id} className="FIG-card" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.25rem' }}
                          onClick={() => handleLinkToExistingPolicy(selectedTriageRecord, p)}>
                          <div className="FIG-card-header">
                            <span className="FIG-card-name" style={{ fontSize: '0.85rem' }}>{p.Carrier || p.LOB || 'Policy'}</span>
                            <span className={`FIG-card-status FIG-card-status--${(p.Status || 'active').toLowerCase()}`}>
                              {p.Status || 'Active'}
                            </span>
                          </div>
                          <span className="FIG-card-contact" style={{ fontSize: '0.78rem' }}>
                            {p.PolicyNumber || ''}{p.EffectiveDate ? ` — ${p.EffectiveDate}` : ''}{p.ExpirationDate ? ` to ${p.ExpirationDate}` : ''}
                          </span>
                        </button>
                      ))}
                      <div style={{ fontSize: '0.78rem', color: '#888', margin: '0.5rem 0 0.25rem' }}>Or:</div>
                    </>
                  )}

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="FIG-save-btn" onClick={() => handleCreateNewFromTriage(selectedTriageRecord)}>
                      + Create New Policy
                    </button>
                    <button className="FIG-back-btn" onClick={() => { setSelectedTriageRecord(null); setMatchingPolicies([]); }}>
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {policiesLoading && <p className="FIG-loading">Loading policies...</p>}

          {!policiesLoading && policiesList.length === 0 && !showAddPolicy && (
            <p className="FIG-empty">No policies yet. Add one above.</p>
          )}

          <div className="FIG-card-list">
            {sortByEffectiveDesc(policiesList)
              .filter(p => {
                if (showInactive) return true;
                const s = (p.Status || '').toLowerCase();
                return s !== 'cancelled' && s !== 'terminated' && s !== 'expired';
              })
              .map(p => {
              const st = (p.Status || '').toLowerCase();
              const isInactive = st === 'cancelled' || st === 'terminated' || st === 'expired';
              const borderColor = st === 'cancelled' ? '#e6a817'
                : (st === 'terminated' || st === 'expired') ? '#999'
                : (st === 'active' || st === 'bound') ? '#2e7d32'
                : '#4B0082';
              return (
              <div key={p.id}>
                <button className="FIG-card" style={{
                  borderLeftColor: borderColor,
                  opacity: isInactive ? 0.7 : 1,
                }} onClick={() => {
                  if (expandedPolicyId === p.id) {
                    setExpandedPolicyId(null);
                    setEditingPolicy(null);
                  } else {
                    setExpandedPolicyId(p.id);
                    setCollapsedSections({ policyInfo: false, financial: true, additionalFees: true, participants: true, endorsements: true });
                    setEditingPolicy({
                      LOB: p.LOB || '', Carrier: p.Carrier || '', NAICCode: p.NAICCode || '',
                      PolicyNumber: p.PolicyNumber || '', ReferenceNumber: p.ReferenceNumber || '',
                      QuoteNumber: p.QuoteNumber || '',
                      EffectiveDate: p.EffectiveDate || '', ExpirationDate: p.ExpirationDate || '',
                      Premium: p.Premium ?? '', Status: p.Status || '', Layer: p.Layer || '',
                      AdmittedStatus: p.AdmittedStatus || '',
                      AgencyBilled: p.AgencyBilled === true, BrokerContact: p.BrokerContact || '',
                      ProgramName: p.ProgramName || '', ProgramCode: p.ProgramCode || '',
                      Underwriter: p.Underwriter || '', UnderwriterOffice: p.UnderwriterOffice || '',
                      StateTax: p.StateTax ?? '', StampingFee: p.StampingFee ?? '', InspectionFee: p.InspectionFee ?? '',
                      BrokerFee: p.BrokerFee ?? '',
                      PolicyFee: p.PolicyFee ?? '', AgencyFee: p.AgencyFee ?? '',
                      AgencyFeePercent: p.AgencyFeePercent ?? 4,
                      CommissionPercent: p.CommissionPercent ?? 10,
                      CommissionAmount: p.CommissionAmount ?? '',
                      WholesaleBroker: p.WholesaleBroker || '',
                      InvoiceFeesDisplay: p.InvoiceFeesDisplay || '',
                      CancellationEffectiveDate: p.CancellationEffectiveDate || '',
                      CancellationReason: p.CancellationReason || '',
                    });
                    loadPolicySubs(p.id);
                    setShowAddPolicy(false);
                  }
                }}>
                  <div className="FIG-card-header">
                    <span className="FIG-card-name">{p.LOB || 'Policy'}</span>
                    <span className={`FIG-card-status FIG-card-status--${(p.Status || 'active').toLowerCase()}`}>
                      {p.Status || 'Active'}
                    </span>
                  </div>
                  <span className="FIG-card-principal">{p.Carrier || ''}</span>
                  <span className="FIG-card-contact">
                    {p.PolicyNumber || ''}{p.Premium ? ` — $${Number(p.Premium).toLocaleString()}` : ''}
                  </span>
                  <span className="FIG-card-contact" style={{ fontSize: '0.8rem' }}>
                    {p.EffectiveDate || ''}{p.ExpirationDate ? ` to ${p.ExpirationDate}` : ''}
                    {p.AgencyBilled === true && <span className="FIG-card-status FIG-card-status--active" style={{ marginLeft: '0.5rem' }}>Agency Billed</span>}
                  </span>
                </button>
                {expandedPolicyId === p.id && editingPolicy && (
                  <div style={{ marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
                    {renderPolicyForm(
                      editingPolicy,
                      setEditingPolicy,
                      (fields) => handleSavePolicy(fields, p.id),
                      () => { setExpandedPolicyId(null); setEditingPolicy(null); },
                      false
                    )}

                    {/* --- Layer Participants --- */}
                    <div style={{ marginTop: '0.75rem' }}>
                      {sectionHeader('Layer Participants', 'participants')}
                      {!collapsedSections.participants && <div style={{ padding: '0.5rem' }}>
                      {participants.length === 0 && <p className="FIG-empty" style={{ margin: '0.5rem 0', textAlign: 'left' }}>No participants.</p>}
                      {participants.map((pt, pi) => (
                        <div key={pt.id} className="FIG-add-form" style={{ marginBottom: '0.5rem', padding: '0.5rem' }}>
                          <div className="FIG-field-grid">
                            <label className="FIG-field"><span className="FIG-field-label">Name</span>
                              <input className="FIG-input" defaultValue={pt.ParticipantName || ''} onBlur={e => saveParticipant({ ...pt, ParticipantName: e.target.value }, pt.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Type</span>
                              <input className="FIG-input" defaultValue={pt.ParticipantType || ''} onBlur={e => saveParticipant({ ...pt, ParticipantType: e.target.value }, pt.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">%</span>
                              <input className="FIG-input" type="number" defaultValue={pt.Percentage ?? ''} onBlur={e => saveParticipant({ ...pt, Percentage: e.target.value }, pt.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Reference #</span>
                              <input className="FIG-input" defaultValue={pt.ReferenceNumber || ''} onBlur={e => saveParticipant({ ...pt, ReferenceNumber: e.target.value }, pt.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Policy #</span>
                              <input className="FIG-input" defaultValue={pt.PolicyNumber || ''} onBlur={e => saveParticipant({ ...pt, PolicyNumber: e.target.value }, pt.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Status</span>
                              <input className="FIG-input" defaultValue={pt.Status || ''} onBlur={e => saveParticipant({ ...pt, Status: e.target.value }, pt.id, p.id)} /></label>
                          </div>
                          <button className="FIG-back-btn" style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#8B0000' }}
                            onClick={() => deleteParticipant(pt.id, p.id)}>Delete</button>
                        </div>
                      ))}
                      <button className="FIG-back-btn" style={{ fontSize: '0.8rem' }}
                        onClick={() => saveParticipant({ ParticipantName: '', ParticipantType: '', Percentage: '', ReferenceNumber: '', PolicyNumber: '', Status: '' }, null, p.id)}>
                        + Add Participant
                      </button>
                      </div>}
                    </div>

                    {/* --- Endorsements --- */}
                    <div style={{ marginTop: '0.75rem' }}>
                      {sectionHeader('Endorsements', 'endorsements',
                        `Current Premium: $${((Number(p.Premium) || 0) + endorsements.reduce((s, e) => s + (Number(e.PremiumChange) || 0), 0)).toLocaleString()}`
                      )}
                      {!collapsedSections.endorsements && <div style={{ padding: '0.5rem' }}>
                      {endorsements.length === 0 && <p className="FIG-empty" style={{ margin: '0.5rem 0', textAlign: 'left' }}>No endorsements.</p>}
                      {endorsements.map((en) => (
                        <div key={en.id} className="FIG-add-form" style={{ marginBottom: '0.5rem', padding: '0.5rem' }}>
                          <div className="FIG-field-grid">
                            <label className="FIG-field"><span className="FIG-field-label">Endorsement #</span>
                              <input className="FIG-input" defaultValue={en.EndorsementNumber || ''} onBlur={e => saveEndorsement({ ...en, EndorsementNumber: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Effective Date</span>
                              <input className="FIG-input" defaultValue={en.EffectiveDate || ''} onBlur={e => saveEndorsement({ ...en, EffectiveDate: e.target.value }, en.id, p.id)} placeholder="MM/DD/YYYY" /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Type</span>
                              <input className="FIG-input" defaultValue={en.EndorsementType || ''} onBlur={e => saveEndorsement({ ...en, EndorsementType: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">$ Premium Change</span>
                              <input className="FIG-input" type="number" defaultValue={en.PremiumChange ?? ''} onBlur={e => saveEndorsement({ ...en, PremiumChange: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Status</span>
                              <input className="FIG-input" defaultValue={en.Status || ''} onBlur={e => saveEndorsement({ ...en, Status: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field FIG-field--wide"><span className="FIG-field-label">Description</span>
                              <input className="FIG-input" defaultValue={en.Description || ''} onBlur={e => saveEndorsement({ ...en, Description: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Request Date</span>
                              <input className="FIG-input" defaultValue={en.RequestDate || ''} onBlur={e => saveEndorsement({ ...en, RequestDate: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field"><span className="FIG-field-label">Issued Date</span>
                              <input className="FIG-input" defaultValue={en.IssuedDate || ''} onBlur={e => saveEndorsement({ ...en, IssuedDate: e.target.value }, en.id, p.id)} /></label>
                            <label className="FIG-field FIG-field--wide"><span className="FIG-field-label">Notes</span>
                              <input className="FIG-input" defaultValue={en.Notes || ''} onBlur={e => saveEndorsement({ ...en, Notes: e.target.value }, en.id, p.id)} /></label>
                          </div>
                          <button className="FIG-back-btn" style={{ marginTop: '0.3rem', fontSize: '0.75rem', color: '#8B0000' }}
                            onClick={() => deleteEndorsement(en.id, p.id)}>Delete</button>
                        </div>
                      ))}
                      <button className="FIG-back-btn" style={{ fontSize: '0.8rem' }}
                        onClick={() => saveEndorsement({ EndorsementNumber: '', EffectiveDate: '', Description: '', EndorsementType: '', PremiumChange: '', Status: '', RequestDate: '', IssuedDate: '', Notes: '' }, null, p.id)}>
                        + Add Endorsement
                      </button>
                      </div>}
                    </div>

                    {/* --- Cancel / Terminate actions --- */}
                    {(st === 'active' || st === 'bound') && (
                      <button className="FIG-add-btn" style={{ marginTop: '0.5rem', background: '#e6a817' }}
                        onClick={() => {
                          const date = window.prompt('Cancellation effective date (MM/DD/YYYY):');
                          if (!date) return;
                          const reason = window.prompt('Reason for cancellation:') || '';
                          handleSavePolicy({ ...editingPolicy, Status: 'Cancelled', CancellationEffectiveDate: date, CancellationReason: reason }, p.id);
                        }}>
                        Cancel Policy
                      </button>
                    )}
                    {st === 'cancelled' && (
                      <button className="FIG-add-btn" style={{ marginTop: '0.5rem', background: '#999' }}
                        onClick={() => handleSavePolicy({ ...editingPolicy, Status: 'Terminated' }, p.id)}>
                        Terminate Policy
                      </button>
                    )}

                    <button
                      className="FIG-add-btn"
                      style={{ marginTop: '0.5rem' }}
                      onClick={() => handleDeletePolicy(p.id)}
                    >
                      Delete Policy
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ) : activeTab === 'Loss Runs' ? (
        (() => {
          const DOT = { green: '#2e7d32', yellow: '#e6a817', red: '#c62828', grey: '#999' };
          const today = new Date();
          const lines = lossRunLines({ ...lossRunData, insured: authInsured, today });
          const season = currentSeason(lossRunData.policies, today);
          const policiesById = new Map(lossRunData.policies.map(p => [String(p.id), p]));
          const handledIdx = resolutionIndex(lossRunData.policies, lossRunData.resolutions, season, policiesById);
          const grid = buildCoverageGrid({ ...lossRunData, today, handled: handledIdx });
          const todo = buildTodo({ grid, sources: lossRunData.sources, policies: lossRunData.policies, requests: lossRunData.requests, insured: authInsured, today, handled: handledIdx });
          const allStates = new Map(lines.flatMap(l => [...l.policyStates.entries()]));
          const summary = openSummary({ policies: lossRunData.policies, states: allStates, resolutions: lossRunData.resolutions, season });
          const resolutionsFor = (line) => splitResolutions(lossRunData.resolutions.filter(r => line.policies.some(p => resolutionCoversPolicy(r, p))), season);
          const directory = lossRunDirectory(lossRunData.sources, lines);
          const minutes = minutesLogged(lossRunData.requests);
          const muted = { fontSize: '0.78rem', color: '#777' };
          const fmtDate = v => dateOnlyOf(v) || '—';
          const sourcesById = new Map(lossRunData.sources.map(s => [String(s.id), s]));
          // Scoreboard per source card (and per grid chip): the same plain words
          // for "paper in vault" and "handled" everywhere (utils/lossRunScoreboard).
          const boardFor = (policies, sourceId) => buildScoreboard({ policies, source: sourcesById.get(sourceId) || null, policyStates: allStates, handled: handledIdx });
          const POPOVER_MAX_ROWS = 5;
          const paperCell = (paper) => (
            <span className="FIG-lossruns-paper">
              <span className="FIG-lossruns-dot" style={{ background: DOT[paper.color] }} />
              {paper.vaultPath
                ? <button type="button" className="FIG-lossruns-pathlink" title={`Copy vault path: ${paper.vaultPath}`} onClick={ev => { ev.stopPropagation(); copyVaultPath(paper.vaultPath); }}>{paper.words}</button>
                : <span>{paper.words}</span>}
              {paper.vaultPath && copiedPath === paper.vaultPath && <span style={muted}>path copied</span>}
            </span>
          );
          const RECORD_LABEL = 'I have a reason — record it';
          const RECORD_HELP = 'Only use this when it is handled and you can point to proof (who said so, when, and where it is written).';
          const authCur = authFormFromInsured(authInsured);
          const authSummary = authCur.OnFile === 'yes'
            ? `On file${authCur.SignedDate ? `, signed ${authCur.SignedDate}` : ', date unknown'}`
            : authCur.OnFile === 'no' ? 'Not on file' : 'Unknown';
          const groupByTerm = (policies) => {
            const m = new Map();
            for (const p of policies) {
              const t = termOf(p) || 'Term unknown';
              if (!m.has(t)) m.set(t, []);
              m.get(t).push(p);
            }
            return [...m.entries()].sort((a, b) => (a[0] === 'Term unknown') - (b[0] === 'Term unknown') || b[0].localeCompare(a[0]));
          };
          const requestsFor = (sourceId) => sortRequestsNewestFirst(
            lossRunData.requests.filter(r => String(r.LossRunSourceId ?? '').trim() === sourceId)
          );
          const canLog = (line) => line.sourceId !== '' && line.doNotContact !== true && line.name !== `Source #${line.sourceId}`;
          // Copy prompt: shown even without an email (the prompt then says the
          // contact is missing) but not when nothing is open for the source.
          const hasOpen = (line) => [...line.policyStates.values()].some(st => st.color !== 'green');
          const canPrompt = (line) => canLog(line) && hasOpen(line);
          const openCard = (sourceId) => {
            setLossRunExpanded(sourceId);
            const el = cardRefs.current[sourceId];
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          };

          const renderForm = (line) => {
            const f = lossRunForm;
            if (!f || f.sourceId !== line.sourceId) return null;
            const v = f.values, e = f.errors || {};
            const allIds = line.policies.map(p => String(p.id));
            const selected = policyIdsToSelected(v.PolicyIds, allIds);
            const hadExplicit = !!(f.original && String(f.original.PolicyIds ?? '').trim());
            const setSelected = (next) => setLossRunField('PolicyIds', selectedToPolicyIds(next, allIds, { hadExplicit }));
            const field = (k, label, input) => (
              <div className="FIG-field" key={k}>
                <span className="FIG-field-label">{label}</span>
                {input}
                {e[k] && <span className="FIG-lossruns-error">{e[k]}</span>}
              </div>
            );
            const text = (k, props = {}) => <input className="FIG-input" value={v[k]} onChange={ev => setLossRunField(k, ev.target.value)} {...props} />;
            const date = (k) => <input className="FIG-input" type="date" value={v[k]} onChange={ev => setLossRunField(k, ev.target.value)} />;
            return (
              <div style={{ marginTop: '0.6rem', padding: '0.7rem', border: '1px solid #d8d1bf', borderRadius: 6, background: '#faf7ee' }} onClick={ev => ev.stopPropagation()}>
                <div style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                  {f.original ? `Edit log entry #${f.original.id}` : 'New log entry'} · {line.name}
                </div>
                <div className="FIG-field-grid">
                  {field('Method', 'METHOD *', (
                    <select className="FIG-input" value={v.Method} onChange={ev => setLossRunField('Method', ev.target.value)}>
                      <option value="">— pick —</option>
                      {LOSS_RUN_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ))}
                  {field('TermsCovered', 'TERMS COVERED', text('TermsCovered', { placeholder: 'e.g. 2022-23, 2023-24' }))}
                  {field('DateRequested', 'DATE REQUESTED', date('DateRequested'))}
                  {field('DateReceived', 'DATE RECEIVED', date('DateReceived'))}
                  {field('ValuedAsOf', 'VALUED AS OF', date('ValuedAsOf'))}
                  {field('MinutesSpent', 'MINUTES SPENT (OPTIONAL)', text('MinutesSpent', { type: 'number', min: 0, step: 1, inputMode: 'numeric' }))}
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">VAULT PATH</span>
                    {text('VaultPath', { placeholder: 'Clients/…/loss_runs.pdf' })}
                  </div>
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">NOTES</span>
                    <textarea className="FIG-input" rows={3} value={v.Notes} onChange={ev => setLossRunField('Notes', ev.target.value)} />
                  </div>
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">
                      COVERS · {selected.size} of {allIds.length} polic{allIds.length === 1 ? 'y' : 'ies'}
                      {' '}<button type="button" className="FIG-add-btn" style={{ padding: '0 0.4rem', fontSize: '0.65rem', background: '#888', marginLeft: 6 }} onClick={() => setSelected(new Set(allIds))}>all</button>
                      {' '}<button type="button" className="FIG-add-btn" style={{ padding: '0 0.4rem', fontSize: '0.65rem', background: '#888' }} onClick={() => setSelected(new Set())}>none</button>
                    </span>
                    {groupByTerm(line.policies).map(([term, pols]) => (
                      <div key={term} style={{ marginBottom: '0.3rem' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.74rem', color: '#555' }}>{term}</div>
                        <div className="FIG-lossruns-covers">
                          {pols.map(p => (
                            <label key={p.id}>
                              <input type="checkbox" checked={selected.has(String(p.id))}
                                onChange={ev => { const next = new Set(selected); if (ev.target.checked) next.add(String(p.id)); else next.delete(String(p.id)); setSelected(next); }} />
                              <span>{p.LOB || '—'} · {(p.PolicyNumber || '').trim() || <em style={muted}>no number</em>} <span style={muted}>· {p.Carrier || '—'}</span></span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    <span style={muted}>All checked saves the entry as covering every policy of this source.</span>
                  </div>
                </div>
                {f.saveError && <p className="FIG-lossruns-error" style={{ marginTop: '0.4rem' }}>Save failed: {f.saveError}</p>}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                  <button className="FIG-add-btn" disabled={lossRunSaving} onClick={submitLossRunForm}>
                    {lossRunSaving ? 'Saving…' : (f.original ? 'Save changes' : 'Save entry')}
                  </button>
                  <button className="FIG-add-btn" style={{ background: '#888' }} disabled={lossRunSaving} onClick={() => setLossRunForm(null)}>Cancel</button>
                  <span style={{ ...muted, alignSelf: 'center' }}>Blank fields are left unset; there is no delete.</span>
                </div>
              </div>
            );
          };

          // A chip opens a small popover anchored to it (outside click / Escape
          // close it) with the full label, the one-line status, and two actions.
          const chipStatusLine = (c) => `${c.tooltip} — ${c.reasons.join(' · ')}${c.handledTooltip ? ` — handled: ${c.handledTooltip}` : ''}`;
          // Chips carry a unique key per cell (line|term|source) from the grid.
          const chip = (c) => {
            const isOpen = chipPopover?.key === c.key;
            return (
              <span key={c.key} className="FIG-lossruns-chipwrap">
                <button type="button" className="FIG-lossruns-chip" title={chipStatusLine(c)} aria-haspopup="dialog" aria-expanded={isOpen}
                  onClick={ev => { ev.stopPropagation(); if (isOpen) closeChipPopover(true); else setChipPopover({ key: c.key, chip: c, anchorEl: ev.currentTarget }); }}>
                  <span className="FIG-lossruns-dot" style={{ background: DOT[c.color] }} />
                  {c.label}{c.handledLabel ? <span style={{ color: '#2e7d32', marginLeft: 4 }}>{c.handledLabel}</span> : null}
                </button>
              </span>
            );
          };
          const popover = (() => {
            if (!chipPopover) return null;
            const c = chipPopover.chip;
            const openIds = c.policyIds.filter(id => allStates.get(id)?.color !== 'green' && !handledIdx.get(id)?.handled);
            const canRecord = openIds.length > 0 && !c.doNotContact && c.sourceId !== '';
            const style = { position: 'fixed', top: popoverPos?.top ?? -9999, left: popoverPos?.left ?? -9999, visibility: popoverPos ? 'visible' : 'hidden' };
            return createPortal(
              <div className="FIG-lossruns-popover" role="dialog" aria-label={c.label} ref={popoverRef} style={style} onClick={ev => ev.stopPropagation()}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>{c.label}{c.handledLabel ? ` ${c.handledLabel}` : ''} <span style={muted}>· {c.lob} · {c.term}</span></div>
                {c.cellOwnership && c.cellOwnership.words && (
                  <div style={{ ...muted, marginBottom: 2, color: c.cellOwnership.kind === 'notMine' ? '#777' : '#4B0082' }}>{c.cellOwnership.words}</div>
                )}
                {(() => {
                  // Per-policy paper + handled words, same helper as the card's scoreboard.
                  const board = boardFor(c.policyIds.map(id => policiesById.get(id)).filter(Boolean), c.sourceId);
                  const shown = board.rows.slice(0, POPOVER_MAX_ROWS);
                  return (
                    <div style={{ marginBottom: '0.4rem' }}>
                      {shown.map(r => (
                        <div key={r.id} className={`FIG-lossruns-poprow${r.open ? ' FIG-lossruns-poprow--open' : ''}`}>
                          <span style={{ fontFamily: 'monospace' }} title={r.label.text}>{r.label.number}</span>
                          {paperCell(r.paper)}
                          <span className={r.handled.muted ? 'FIG-lossruns-muted' : undefined}>{r.handled.words}</span>
                        </div>
                      ))}
                      {board.rows.length > shown.length && <div style={muted}>+ {board.rows.length - shown.length} more — open the card</div>}
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button type="button" className="FIG-add-btn" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }} autoFocus
                    onClick={() => { closeChipPopover(false); openCard(c.sourceId); }}>Open {c.name} card</button>
                  {canRecord && (
                    <button type="button" className="FIG-btn-outline" onClick={() => { closeChipPopover(false); openResForm(c.sourceId, openIds, season); }}>
                      {RECORD_LABEL}
                    </button>
                  )}
                  <button type="button" className="FIG-btn-outline" onClick={() => closeChipPopover(true)}>Close</button>
                </div>
                {canRecord && <div style={{ ...muted, marginTop: '0.3rem' }}>{RECORD_HELP}</div>}
              </div>,
              document.body,
            );
          })();

          const renderResForm = (line) => {
            const f = resForm;
            if (!f || f.sourceId !== line.sourceId) return null;
            const v = f.values, e = f.errors || {};
            const allIds = line.policies.map(p => String(p.id));
            const selected = policyIdsToSelected(v.PolicyIds || '__none__', allIds);
            const setSel = (next) => setResField('PolicyIds', allIds.filter(id => next.has(id)).join(','));
            const field = (k, label, input) => (
              <div className="FIG-field" key={k}>
                <span className="FIG-field-label">{label}</span>{input}
                {e[k] && <span className="FIG-lossruns-error">{e[k]}</span>}
              </div>
            );
            const text = (k, props = {}) => <input className="FIG-input" value={v[k]} onChange={ev => setResField(k, ev.target.value)} {...props} />;
            const handledStatus = ['Satisfied', 'Not required'].includes(v.Status);
            return (
              <div style={{ marginTop: '0.6rem', padding: '0.7rem', border: '1px solid #d8d1bf', borderRadius: 6, background: '#f3f7ee' }} onClick={ev => ev.stopPropagation()}>
                <div style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.85rem', marginBottom: '0.2rem' }}>{RECORD_LABEL} · {line.name}</div>
                <div style={{ ...muted, marginBottom: '0.5rem' }}>{RECORD_HELP}</div>
                <div className="FIG-field-grid">
                  {field('RenewalYear', 'RENEWAL YEAR *', text('RenewalYear', { placeholder: season || 'YYYY', inputMode: 'numeric' }))}
                  {field('Status', 'STATUS *', (
                    <select className="FIG-input" value={v.Status} onChange={ev => setResField('Status', ev.target.value)}>
                      <option value="">— pick —</option>{RESOLUTION_STATUSES.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>))}
                  {field('Reason', 'REASON *', (
                    <select className="FIG-input" value={v.Reason} onChange={ev => setResField('Reason', ev.target.value)}>
                      <option value="">— pick —</option>{RESOLUTION_REASONS.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>))}
                  {field('DecidedBy', handledStatus ? 'WHO SAID SO *' : 'WHO SAID SO', text('DecidedBy', { placeholder: 'name and company' }))}
                  {field('DecidedDate', handledStatus ? 'WHEN *' : 'WHEN', <input className="FIG-input" type="date" value={v.DecidedDate} onChange={ev => setResField('DecidedDate', ev.target.value)} />)}
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">{handledStatus ? 'EVIDENCE *' : 'EVIDENCE'}</span>
                    {text('Evidence', { placeholder: 'email subject and date, or a FIGVault path' })}
                    {e.Evidence && <span className="FIG-lossruns-error">{e.Evidence}</span>}
                  </div>
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">NOTES{v.Reason === 'Other' || v.Status === 'Still open' ? ' *' : ''}</span>
                    <textarea className="FIG-input" rows={2} value={v.Notes} onChange={ev => setResField('Notes', ev.target.value)} />
                    {e.Notes && <span className="FIG-lossruns-error">{e.Notes}</span>}
                  </div>
                  <div className="FIG-field FIG-field--wide">
                    <span className="FIG-field-label">COVERS * · {selected.size} of {allIds.length}
                      {' '}<button type="button" className="FIG-add-btn" style={{ padding: '0 0.4rem', fontSize: '0.65rem', background: '#888', marginLeft: 6 }} onClick={() => setSel(new Set(allIds))}>all</button>
                      {' '}<button type="button" className="FIG-add-btn" style={{ padding: '0 0.4rem', fontSize: '0.65rem', background: '#888' }} onClick={() => setSel(new Set())}>none</button>
                    </span>
                    {groupByTerm(line.policies).map(([term, pols]) => (
                      <div key={term} style={{ marginBottom: '0.3rem' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.74rem', color: '#555' }}>{term}</div>
                        <div className="FIG-lossruns-covers">
                          {pols.map(p => (
                            <label key={p.id}>
                              <input type="checkbox" checked={selected.has(String(p.id))}
                                onChange={ev => { const next = new Set(selected); if (ev.target.checked) next.add(String(p.id)); else next.delete(String(p.id)); setSel(next); }} />
                              <span>{p.LOB || '—'} · {(p.PolicyNumber || '').trim() || <em style={muted}>no number</em>}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                    {e.PolicyIds && <span className="FIG-lossruns-error">{e.PolicyIds}</span>}
                  </div>
                </div>
                {f.saveError && <p className="FIG-lossruns-error" style={{ marginTop: '0.4rem' }}>Save failed: {f.saveError}</p>}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                  <button className="FIG-add-btn" disabled={resSaving} onClick={submitResForm}>{resSaving ? 'Saving…' : 'Save'}</button>
                  <button className="FIG-add-btn" style={{ background: '#888' }} disabled={resSaving} onClick={() => setResForm(null)}>Cancel</button>
                  <span style={{ ...muted, alignSelf: 'center' }}>Saves a new row; colors do not change. A correction is another row.</span>
                </div>
              </div>
            );
          };

          return (
            <div className="FIG-lossruns">
              {popover}
              {/* Header: counts over required-line cells, actions */}
              <div className="FIG-lossruns-header">
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.5rem', marginRight: '0.5rem' }}
                  title={`Open = policies on required or letter-accepted lines that are not green and not handled this renewal${season ? ` (season ${season})` : ''}. Colors answer only whether the paper is on file.`}>
                  <strong style={{ fontSize: '1.05rem' }}>{summary.open === 0 ? 'Nothing open' : `open: ${summary.open}`}</strong>
                  <span style={muted}>handled: {summary.handled}{season ? ` · season ${season}` : ''}</span>
                </span>
                <span title="Counts cover cells on lines tagged “Loss runs required” only (one cell = one line × one term)." style={{ display: 'inline-flex', gap: '0.9rem', alignItems: 'center' }}>
                  <span style={muted}>required lines:</span>
                  {['green', 'yellow', 'red', 'grey'].map(c => (
                    <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                      <span className="FIG-lossruns-dot" style={{ background: DOT[c] }} />
                      <strong>{grid.counts[c]}</strong> <span style={muted}>{c}</span>
                    </span>
                  ))}
                </span>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button className="FIG-add-btn" style={{ background: '#4B0082' }} disabled={!lossRunLoaded}
                    title="Copy a prompt for Claude chat covering every open source"
                    onClick={() => copyPrompt({ scope: 'all open' })}>
                    {promptState?.scope === 'all open' && promptState.copied ? 'Prompt copied' : 'Copy prompt: everything open'}
                  </button>
                  <button className="FIG-add-btn" style={{ background: '#4B0082' }} disabled={handoffBusy || !lossRunLoaded}
                    title="Download the insurance history handoff (no premiums, fees or commissions)"
                    onClick={downloadHandoff}>
                    {handoffBusy ? 'Building…' : 'Handoff PDF'}
                  </button>
                  <button className="FIG-add-btn" style={{ background: '#888' }} disabled={lossRunLoading}
                    onClick={() => loadLossRuns()}>
                    {lossRunLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </span>
              </div>
              {handoffError && <p className="FIG-lossruns-error">Handoff PDF failed: {handoffError}</p>}
              {(lossRunData.warnings || []).map((w, i) => <p key={i} className="FIG-lossruns-error" style={{ color: '#8a6d1a' }}>{w}</p>)}
              {promptState?.scope === 'all open' && promptState.blocked && <p className="FIG-lossruns-error">No prompt: {promptState.blocked}.</p>}
              {promptState?.scope === 'all open' && promptState.error && <p className="FIG-lossruns-error">Clipboard unavailable: {promptState.error}</p>}

              {lossRunError && <p className="FIG-empty" style={{ color: '#c62828' }}>Could not load loss run data: {lossRunError}</p>}
              {lossRunLoading && !lossRunLoaded && <p className="FIG-empty">Loading loss run status…</p>}

              {/* What to do next */}
              {lossRunLoaded && (
                <div className="FIG-card" style={{ borderLeft: '4px solid #4B0082', cursor: 'default' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: '0.25rem' }}>What to do next</div>
                  <ol className="FIG-lossruns-todo">
                    {todo.map((t, i) => (
                      <li key={i}>
                        {t.text}
                        {t.canPrompt && (
                          <button className="FIG-add-btn" style={{ marginLeft: 8, padding: '0.1rem 0.5rem', fontSize: '0.72rem', background: '#4B0082' }}
                            onClick={() => copyPrompt({ scope: { sourceId: t.sourceId }, sourceId: t.sourceId })}>
                            {promptState?.sourceId === t.sourceId && promptState.copied ? 'Copied' : 'Copy prompt'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Loss run authorization letter — the insured's, kept on FIG_Insureds. */}
              <div className="FIG-card" style={{ borderLeft: '4px solid #B8860B', cursor: 'default' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                  <span style={{ fontWeight: 700 }}>Loss run authorization</span>
                  <span>{authSummary}</span>
                  {authCur.VaultPath && <span style={{ ...muted, fontFamily: 'monospace', wordBreak: 'break-all' }}>{authCur.VaultPath}</span>}
                  {!authForm && (
                    <button className="FIG-add-btn" style={{ marginLeft: 'auto', padding: '0.2rem 0.6rem', fontSize: '0.75rem', background: '#888' }}
                      onClick={() => setAuthForm({ values: authFormFromInsured(authInsured), errors: {}, saveError: null })}>Edit</button>
                  )}
                </div>
                {authForm && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div className="FIG-field-grid">
                      <div className="FIG-field">
                        <span className="FIG-field-label">ON FILE</span>
                        <select className="FIG-input" value={authForm.values.OnFile}
                          onChange={ev => setAuthForm(p => ({ ...p, values: { ...p.values, OnFile: ev.target.value }, errors: { ...p.errors, OnFile: undefined } }))}>
                          {AUTH_ON_FILE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        {authForm.errors.OnFile && <span className="FIG-lossruns-error">{authForm.errors.OnFile}</span>}
                      </div>
                      <div className="FIG-field">
                        <span className="FIG-field-label">SIGNED DATE</span>
                        <input className="FIG-input" type="date" value={authForm.values.SignedDate}
                          onChange={ev => setAuthForm(p => ({ ...p, values: { ...p.values, SignedDate: ev.target.value }, errors: { ...p.errors, SignedDate: undefined } }))} />
                        {authForm.errors.SignedDate && <span className="FIG-lossruns-error">{authForm.errors.SignedDate}</span>}
                      </div>
                      <div className="FIG-field FIG-field--wide">
                        <span className="FIG-field-label">VAULT PATH</span>
                        <input className="FIG-input" value={authForm.values.VaultPath} placeholder="Clients/…/authorization_signed.pdf"
                          onChange={ev => setAuthForm(p => ({ ...p, values: { ...p.values, VaultPath: ev.target.value } }))} />
                      </div>
                    </div>
                    {authForm.saveError && <p className="FIG-lossruns-error" style={{ marginTop: '0.4rem' }}>Save failed: {authForm.saveError}</p>}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button className="FIG-add-btn" disabled={authSaving} onClick={submitAuthForm}>{authSaving ? 'Saving…' : 'Save'}</button>
                      <button className="FIG-add-btn" style={{ background: '#888' }} disabled={authSaving} onClick={() => setAuthForm(null)}>Cancel</button>
                      <span style={{ ...muted, alignSelf: 'center' }}>Unknown and blank fields are left unset.</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Coverage grid: lines × terms */}
              {lossRunLoaded && grid.rows.length === 0 && (
                <p className="FIG-empty">No policies for this client yet, so there is nothing to request.</p>
              )}
              {lossRunLoaded && grid.rows.length > 0 && (
                <div className="FIG-lossruns-gridwrap" ref={gridWrapRef}>
                  <table className="FIG-lossruns-table">
                    <thead>
                      {/* Era band: consecutive terms by dominant AgencyOfRecord (utils/lossRunEra). */}
                      <tr className="FIG-lossruns-era">
                        <th aria-hidden="true" />
                        {grid.eras.map((e, i) => (
                          <th key={i} colSpan={e.span} className={e.own ? 'FIG-lossruns-era--own' : 'FIG-lossruns-era--other'}
                            title={e.own ? 'You were the agent of record for these terms; no client letter needed' : `${e.name} was the agent of record for these terms; a client letter is needed`}>
                            {e.label}
                          </th>
                        ))}
                      </tr>
                      <tr>
                        <th>Line of coverage</th>
                        {grid.terms.map(t => <th key={t}>{t}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {grid.rows.map((r, ri) => (
                        <tr key={r.lob} className={[r.need === 'notUsual' ? 'FIG-lossruns-row--muted' : '', ri > 0 && grid.rows[ri - 1].need !== r.need ? 'FIG-lossruns-row--group' : ''].filter(Boolean).join(' ') || undefined}>
                          <td>
                            <strong>{r.lob}</strong>
                            <span className="FIG-lossruns-need">{r.needLabel}</span>
                          </td>
                          {grid.terms.map(t => {
                            const cell = r.cells[t];
                            const tint = !cell ? '' : cell.ownership.kind === 'mine' ? 'FIG-lossruns-cell--mine' : cell.ownership.kind === 'mixed' ? 'FIG-lossruns-cell--mixed' : '';
                            return (
                              <td key={t} className={tint || undefined} title={cell && cell.ownership.kind !== 'notMine' ? cell.ownership.words : undefined}>
                                {!cell ? <span className="FIG-lossruns-empty">No policy on record</span> : cell.chips.map(c => chip(c))}
                                {cell && cell.agencyTag && <span className="FIG-lossruns-agencytag">{cell.agencyTag}</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="FIG-lossruns-legend">
                    <span><span className="FIG-lossruns-swatch FIG-lossruns-swatch--own" /> Your years: you were the agent, no client letter needed</span>
                    <span>·</span>
                    <span><span className="FIG-lossruns-swatch" /> Another agency's years: client letter needed</span>
                    <span>·</span>
                    <span><span className="FIG-lossruns-dot" style={{ background: DOT.green }} /> current on file</span>
                    <span><span className="FIG-lossruns-dot" style={{ background: DOT.yellow }} /> old copy</span>
                    <span><span className="FIG-lossruns-dot" style={{ background: DOT.red }} /> nothing on file</span>
                    <span>·</span>
                    <span><span style={{ color: '#2e7d32', fontWeight: 700 }}>✓</span> handled this renewal</span>
                  </div>
                </div>
              )}

              {/* Source cards — collapsed by default */}
              <div className="FIG-lossruns-grid">
                {lines.map(line => {
                  const open = lossRunExpanded === line.sourceId;
                  const logRows = line.sourceId === '' ? [] : requestsFor(line.sourceId);
                  const ps = promptState?.sourceId === line.sourceId ? promptState : null;
                  const board = boardFor(line.policies, line.sourceId);
                  const { history: priorResolutions } = resolutionsFor(line);
                  return (
                    <div key={line.sourceId || '__unassigned'} ref={el => { cardRefs.current[line.sourceId] = el; }}
                      className={`FIG-card${open ? ' FIG-lossruns-card--open' : ''}`}
                      style={{ borderLeft: `4px solid ${DOT[line.color]}`, cursor: 'pointer' }}
                      onClick={() => { setLossRunExpanded(open ? null : line.sourceId); if (open && lossRunForm?.sourceId === line.sourceId) setLossRunForm(null); }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span className="FIG-lossruns-dot" style={{ background: DOT[line.color] }} />
                        <span style={{ fontWeight: 700 }}>{line.name}</span>
                        {line.kind && <span style={muted}>{line.kind}</span>}
                        <span style={muted}>{line.policyCount} polic{line.policyCount === 1 ? 'y' : 'ies'}{line.terms.length ? `, ${line.terms.join(', ')}` : ''}</span>
                        {logRows.length > 0 && <span style={muted}>{logRows.length} log entr{logRows.length === 1 ? 'y' : 'ies'}</span>}
                        {(canLog(line) || canPrompt(line)) && (
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '0.4rem' }}>
                            {canPrompt(line) && (
                              <button className="FIG-add-btn" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', background: '#4B0082' }}
                                onClick={ev => { ev.stopPropagation(); copyPrompt({ scope: { sourceId: line.sourceId }, sourceId: line.sourceId }); }}>
                                {ps?.copied ? 'Prompt copied' : 'Copy prompt'}
                              </button>
                            )}
                            {canLog(line) && (
                              <button className="FIG-add-btn" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                                onClick={ev => { ev.stopPropagation(); openLossRunForm(line, null); }}>Log</button>
                            )}
                            {line.sourceId !== '' && line.doNotContact !== true && [...line.policyStates.values()].some(st => st.color !== 'green') && (
                              <button className="FIG-btn-outline"
                                onClick={ev => { ev.stopPropagation(); openResForm(line.sourceId, line.policies.filter(p => line.policyStates.get(String(p.id))?.color !== 'green' && !handledIdx.get(String(p.id))?.handled).map(p => String(p.id)), season); }}>
                                {RECORD_LABEL}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#555' }}>Lines: {line.lines.length ? line.lines.join(', ') : '—'}</div>
                      {!open && (
                        <div style={{ fontSize: '0.8rem', color: line.color === 'grey' ? '#666' : '#333' }}>
                          {scoreboardSummary(board.counts, { oldCopies: true })}
                          {line.greyReasons.length > 0 && <span style={muted}> · {line.greyReasons.join(', ')}</span>}
                        </div>
                      )}
                      {line.authNote && <div style={{ fontSize: '0.78rem', color: '#8a6d1a' }}>{line.authNote}</div>}
                      {ps && (ps.copied || ps.blocked || ps.error) && (
                        <div style={{ marginTop: '0.4rem', padding: '0.5rem 0.6rem', border: '1px solid #d8d1bf', borderRadius: 4, background: '#faf7ee', fontSize: '0.8rem' }}
                          onClick={ev => ev.stopPropagation()}>
                          {ps.blocked && <div className="FIG-lossruns-error">No prompt: {ps.blocked}.</div>}
                          {ps.error && <div className="FIG-lossruns-error">Clipboard unavailable: {ps.error}</div>}
                          {ps.copied && <div>Prompt copied. Paste it into Claude chat; nothing is sent from here. Once the request has gone out, record it:</div>}
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                            {ps.copied && canLog(line) && (
                              <button className="FIG-add-btn" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                                onClick={() => { openLossRunForm(line, null, { Method: 'Loss Run', DateRequested: dateOnlyOf(new Date()) }); setPromptState(null); }}>
                                Log this request
                              </button>
                            )}
                            <button className="FIG-add-btn" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', background: '#888' }} onClick={() => setPromptState(null)}>Dismiss</button>
                          </div>
                        </div>
                      )}

                      {open && (
                        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #eee7d4', fontSize: '0.85rem' }} onClick={ev => ev.stopPropagation()}>
                          {/* Scoreboard: one row per eligible policy, newest term first. */}
                          <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{scoreboardSummary(board.counts)}</div>
                          <table className="FIG-lossruns-score">
                            <thead>
                              <tr>
                                <th>Line</th>
                                <th>Policy</th>
                                <th>Paper in vault</th>
                                <th>Handled this renewal{season ? ` (${season})` : ''}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {board.groups.map(g => (
                                <Fragment key={g.term}>
                                  <tr className="FIG-lossruns-score-term"><td colSpan={4}>{g.term}</td></tr>
                                  {g.rows.map(r => (
                                    <tr key={r.id} className={r.open ? 'FIG-lossruns-score-row--open' : undefined}>
                                      <td>{r.line}</td>
                                      <td>
                                        {r.label.carrier} <span style={{ fontFamily: 'monospace' }}>{r.label.number}</span>
                                        {r.label.partOf && <span style={muted}> (part of {r.label.partOf})</span>}
                                      </td>
                                      <td>{paperCell(r.paper)}</td>
                                      <td className={r.handled.muted ? 'FIG-lossruns-muted' : undefined}>{r.handled.words}</td>
                                    </tr>
                                  ))}
                                </Fragment>
                              ))}
                            </tbody>
                          </table>

                          {renderForm(line)}
                          {renderResForm(line)}

                          {line.sourceId !== '' && (
                            <details className="FIG-lossruns-section">
                              <summary>Contact and notes</summary>
                              <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '0.2rem 0.8rem', marginTop: '0.3rem' }}>
                                <span style={muted}>Contact</span><span>{line.contactName || '—'}</span>
                                <span style={muted}>Email</span><span>{line.contactEmail || '—'}</span>
                                <span style={muted}>Phone</span><span>{line.contactPhone || '—'}</span>
                                <span style={muted}>Default method</span><span>{line.defaultMethod || '—'}</span>
                                <span style={muted}>Authorization</span>
                                <span>
                                  {line.authorizationNeed === 'needed' ? 'Required' : line.authorizationNeed === 'not needed' ? 'Not required' : 'Unknown'}
                                  {line.authorizationCounts && line.authorizationCounts.total > 0 && (
                                    <span style={muted}> · {line.authorizationCounts.needing} of {line.authorizationCounts.total} written by another agency{line.authorizationCounts.unknown ? `, ${line.authorizationCounts.unknown} unknown` : ''}{line.requiresInsuredAuthorization !== null ? ` · source override: ${line.requiresInsuredAuthorization ? 'required' : 'not required'}` : ''}</span>
                                  )}
                                </span>
                                <span style={muted}>Notes</span><span style={{ whiteSpace: 'pre-wrap' }}>{line.notes || '—'}</span>
                              </div>
                            </details>
                          )}

                          {line.sourceId !== '' && (
                            <details className="FIG-lossruns-section">
                              <summary>Log ({logRows.length} {logRows.length === 1 ? 'entry' : 'entries'})</summary>
                              {logRows.length === 0 && <div style={{ ...muted, marginTop: '0.3rem' }}>No log entries for this source yet.</div>}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '0.3rem' }}>
                                {logRows.map(r => {
                                  const covered = policyIdsToSelected(r.PolicyIds, line.policies.map(p => String(p.id)));
                                  return (
                                    <div key={r.id} className="FIG-lossruns-log-row">
                                      <div>
                                        <div><strong>{r.Method || 'Method unknown'}</strong>{r.TermsCovered ? <span style={muted}> · {r.TermsCovered}</span> : null}</div>
                                        <div style={muted}>
                                          Requested {fmtDate(r.DateRequested)} · Received {fmtDate(r.DateReceived)} · Valued {fmtDate(r.ValuedAsOf)}
                                          {' · '}{r.MinutesSpent === null || r.MinutesSpent === undefined || r.MinutesSpent === '' ? 'minutes not recorded' : `${r.MinutesSpent} min`}
                                          {' · '}{String(r.PolicyIds ?? '').trim() ? `covers ${covered.size} of ${line.policies.length} policies` : 'covers all policies of this source'}
                                        </div>
                                        {r.VaultPath && <div style={{ ...muted, fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.VaultPath}</div>}
                                        {r.Notes && <div style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{r.Notes}</div>}
                                      </div>
                                      <button className="FIG-add-btn" style={{ background: '#888', padding: '0.2rem 0.6rem', fontSize: '0.75rem', alignSelf: 'start' }}
                                        onClick={() => openLossRunForm(line, r)}>Edit</button>
                                    </div>
                                  );
                                })}
                              </div>
                            </details>
                          )}

                          {priorResolutions.length > 0 && (
                            <details className="FIG-lossruns-section">
                              <summary>How it was handled before ({priorResolutions.length})</summary>
                              {priorResolutions.map(r => <div key={r.id} style={{ fontSize: '0.8rem', padding: '0.1rem 0 0.1rem 0.6rem' }}><span style={muted}>{r.RenewalYear || '—'} renewal — </span>{resolutionSentence(r, policiesById)}</div>)}
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {lossRunLoaded && (
                <div>
                  <div onClick={() => setLossRunDirOpen(v => !v)}
                    style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px', ...muted }}>
                    <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: lossRunDirOpen ? 'rotate(0deg)' : 'rotate(-90deg)', fontSize: '0.55rem' }}>&#9660;</span>
                    Directory · {directory.length} source{directory.length === 1 ? '' : 's'} with no policies assigned for this client
                  </div>
                  {lossRunDirOpen && (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.82rem' }}>
                      {directory.length === 0 && <p className="FIG-empty">Every source in the directory has a policy assigned here.</p>}
                      {directory.map(s => (
                        <div key={s.id} style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', padding: '0.2rem 0.6rem' }}>
                          <span style={{ fontWeight: 600 }}>{s.Title}</span>
                          <span style={muted}>{s.Kind || '—'}</span>
                          <span style={muted}>{s.ContactEmail || s.ContactPhone || 'no contact on file'}</span>
                          <span style={muted}>{s.DefaultMethod || ''}</span>
                          {s.DoNotContact === true && <span style={{ color: '#c62828', fontWeight: 600 }}>Do not contact</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {lossRunLoaded && minutes.total > 0 && (
                <div style={{ ...muted, textAlign: 'right' }}>
                  Minutes logged: {minutes.total} ({minutes.counted} entr{minutes.counted === 1 ? 'y' : 'ies'}{minutes.missing ? `; ${minutes.missing} with no minutes` : ''})
                </div>
              )}
            </div>
          );
        })()
      ) : activeTab === 'Application' ? (
        (() => {
          // Resolve the active policy for Application view
          const appPolicy = initialPolicy
            || acordPolicies.find(p => p.id === initialPolicyId)
            || currentFirst(acordPolicies)[0]
            || null;

          // Market readiness calculation (mirrors FIGRenewalPipeline getMarketReadiness)
          const mrHas3 = !!(appPolicy?.ThreeYearPriorCarrier && appPolicy?.ThreeYearPriorContact);
          const mrHas2 = !!(appPolicy?.TwoYearPriorCarrier && appPolicy?.TwoYearPriorContact);
          const mrHas1 = !!(appPolicy?.PriorCarrierName && appPolicy?.PriorCarrierContact);
          const mrHasCurrent = !!(appPolicy?.Carrier);
          const mrYears = (mrHasCurrent ? 1 : 0) + (mrHas1 ? 1 : 0) + (mrHas2 ? 1 : 0) + (mrHas3 ? 1 : 0);
          const mrLevel = mrYears >= 4 ? 'green' : mrYears >= 3 ? 'orange' : 'red';
          const mrColor = mrLevel === 'green' ? '#22c55e' : mrLevel === 'orange' ? '#f59e0b' : '#ef4444';
          const mrLabel = mrLevel === 'green' ? 'Ready to shop' : mrLevel === 'orange' ? 'Switching possible' : 'Stay & renew';

          // Relevant ACORD forms for this policy's LOB
          const lobForms = ACORD_LOB_MAP[(appPolicy?.LOB || '')] || [];
          const relevantForms = ['125', ...lobForms, '25'].map(k => ACORD_FORMS[k]).filter(Boolean);

          // Days until expiration
          const appExpDt = parseLocalDate(appPolicy?.ExpirationDate);
          const appTodayDt = new Date(); appTodayDt.setHours(0, 0, 0, 0);
          const appDays = appExpDt
            ? Math.ceil((appExpDt - appTodayDt) / (1000 * 60 * 60 * 24))
            : null;
          const appDaysColor = appDays !== null
            ? (appDays <= 30 ? '#ef4444' : appDays <= 90 ? '#f59e0b' : '#22c55e')
            : '#555';

          const fmtAppDate = (d) => {
            if (!d) return '—';
            const dt = new Date(d);
            return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
          };

          if (!appPolicy) {
            return (
              <div className="FIG-app-empty">
                <p className="FIG-coming-soon">No policy selected — go back to Dashboard and click a gauge.</p>
              </div>
            );
          }

          return (
            <div className="FIG-app-workspace">
              {/* ---- POLICY CONTEXT BAR ---- */}
              <div className="FIG-app-context">
                <div className="FIG-app-context-led" style={{ background: appDaysColor, boxShadow: `0 0 6px ${appDaysColor}` }} />
                <div className="FIG-app-context-fields">
                  <div className="FIG-app-context-primary">
                    <span className="FIG-app-context-lob">{appPolicy.LOB || 'Policy'}</span>
                    <span className="FIG-app-context-carrier">{appPolicy.Carrier || '—'}</span>
                  </div>
                  <div className="FIG-app-context-secondary">
                    <span>#{appPolicy.PolicyNumber || '—'}</span>
                    <span>{fmtAppDate(appPolicy.EffectiveDate)} → {fmtAppDate(appPolicy.ExpirationDate)}</span>
                    {appDays !== null && <span style={{ color: appDaysColor, fontWeight: 700 }}>{appDays}d</span>}
                    {appPolicy.Premium && <span style={{ color: '#22c55e' }}>${Number(appPolicy.Premium).toLocaleString()}</span>}
                  </div>
                </div>
              </div>

              {/* ---- TWO COLUMN LAYOUT ---- */}
              <div className="FIG-app-columns">
                {/* LEFT: Documents + ACORD Generator */}
                <div className="FIG-app-col FIG-app-col--left">
                  <div className="FIG-app-section">
                    <div className="FIG-app-section-title">ACORD GENERATOR</div>
                    <div className="FIG-app-acord-list">
                      {relevantForms.map(f => (
                        <button key={f.view} className="FIG-app-acord-btn"
                          onClick={() => { setActiveTab('ACORDs'); }}>
                          <span className="FIG-app-acord-name">{f.name}</span>
                          <span className="FIG-app-acord-desc">{f.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="FIG-app-section">
                    <div className="FIG-app-section-title">POLICY DOCUMENTS</div>
                    <button className="FIG-app-doc-btn"
                      onClick={() => {
                        const folder = lobToVaultFolder(appPolicy.LOB);
                        if (onBack) onBack();
                      }}>
                      Open in Vault →
                    </button>
                  </div>
                </div>

                {/* RIGHT: Market Readiness + Navigation */}
                <div className="FIG-app-col FIG-app-col--right">
                  <div className="FIG-app-section">
                    <div className="FIG-app-section-title">MARKET READINESS</div>
                    <div className="FIG-app-mr-status">
                      <div className="FIG-app-mr-led" style={{ background: mrColor, boxShadow: `0 0 8px ${mrColor}` }} />
                      <span className="FIG-app-mr-label" style={{ color: mrColor }}>{mrLabel}</span>
                    </div>
                    <div className="FIG-app-mr-rows">
                      {[
                        { label: 'Current', carrier: appPolicy.Carrier, contact: '—' },
                        { label: 'Prior', carrier: appPolicy.PriorCarrierName, contact: appPolicy.PriorCarrierContact },
                        { label: '2yr back', carrier: appPolicy.TwoYearPriorCarrier, contact: appPolicy.TwoYearPriorContact },
                        { label: '3yr back', carrier: appPolicy.ThreeYearPriorCarrier, contact: appPolicy.ThreeYearPriorContact },
                      ].map((row, i) => (
                        <div key={i} className="FIG-app-mr-row">
                          <span className={row.carrier ? 'FIG-app-mr-check--yes' : 'FIG-app-mr-check--no'}>
                            {row.carrier ? '●' : '○'}
                          </span>
                          <span className="FIG-app-mr-year">{row.label}</span>
                          <span className="FIG-app-mr-val">{row.carrier || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="FIG-app-section">
                    <button className="FIG-app-back-btn" onClick={onBack}>
                      ← Back to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="FIG-tab-placeholder">
          <p className="FIG-coming-soon">{activeTab} — coming soon</p>
        </div>
      )}
    </div>
  );
}

export default FIGInsured;
